import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { getAllCountries } from '@fey/core'
import logoUrl from './assets/logo.png'
import { listLandingIntegrationCatalog, submitWaitlist, sendWaitlistEmail, trackEvent } from './api'
import {
  TIERS,
  SAVINGS_RATES,
  PRICING_COMPARISON_ROWS as COMPARISON_ROWS,
  PPP_MARKETS,
  DEFAULT_MARKET_ID as CORE_DEFAULT_MARKET_ID,
  FALLBACK_FX_RATES as CORE_FALLBACK_FX_RATES,
  DEFAULT_CALCULATOR_INPUTS,
  SUPPLIER_NETWORK_PRO,
  calculateSubscription,
  calculateSavings,
  applyPpp,
  usdToLocal,
  getLocalizedBranchPrice,
  getMarketProfile as coreGetMarketProfile,
  formatMoney,
  type MarketProfile,
  type CalculatorInputs,
} from './pricing'

/* ── Signup modal context ── */

interface SignupForm {
  name: string
  email: string
  phone: string
  dial_code: string
  branches: string
  referral: string
  pharmacy_name: string
  role: string
  current_system: string
  current_system_other: string
  demo_goal: string[]
  demo_slot_1: string
  demo_slot_2: string
  demo_slot_3: string
}

const EMPTY_FORM: SignupForm = {
  name: '',
  email: '',
  phone: '',
  dial_code: '+234',
  branches: '1',
  referral: '',
  pharmacy_name: '',
  role: '',
  current_system: '',
  current_system_other: '',
  demo_goal: [],
  demo_slot_1: '',
  demo_slot_2: '',
  demo_slot_3: '',
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^(?=.*\d)[\d\s\-()]{7,}$/
const BRANCH_MIN = 1
const BRANCH_SLIDER_MAX = 50
const BRANCH_INPUT_MAX = 999
const DEMO_MAX_SLOTS = 3
const DEMO_FOCUS_OPTIONS = [
  { value: 'Low-stock alerts and stock visibility', label: 'Low-stock alerts and stock visibility' },
  { value: 'Procurement automation', label: 'Procurement automation' },
  { value: 'AI assistant workflows', label: 'AI assistant workflows' },
  { value: 'Multi-branch coordination', label: 'Multi-branch coordination' },
  { value: 'Inventory import and setup', label: 'Inventory import and setup' },
  { value: 'Reports and business insights', label: 'Reports and business insights' },
]

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'pharmacist', label: 'Pharmacist' },
  { value: 'manager', label: 'Manager' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'other', label: 'Other' },
]

const NON_SOFTWARE_SYSTEMS = [
  { value: 'pen_paper', label: 'Pen & paper' },
  { value: 'none', label: 'Nothing yet' },
  { value: 'other', label: 'Other' },
]

// Catalog keys that aren't actual systems (import methods, generic labels, or hidden internal entries)
const EXCLUDED_SYSTEM_KEYS = new Set(['csv', 'api', 'pms', 'pen_paper'])


function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function normalizeBranchCount(raw: string, max: number): string {
  const digits = raw.replace(/\D/g, '')
  const parsed = Number.parseInt(digits, 10)
  if (!Number.isFinite(parsed)) return String(BRANCH_MIN)
  const clamped = Math.max(BRANCH_MIN, Math.min(max, parsed))
  return String(clamped)
}

function getBranchCount(raw: string): number {
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return BRANCH_MIN
  return Math.max(BRANCH_MIN, parsed)
}

const DIAL_CODES = getAllCountries().map((entry) => ({
  code: entry.dialCode,
  country: entry.name,
  flag: entry.flag,
}))

function DialCodeSelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    setTimeout(() => searchRef.current?.focus(), 50)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = search
    ? DIAL_CODES.filter(
      (d) =>
        d.country.toLowerCase().includes(search.toLowerCase()) ||
        d.code.includes(search),
    )
    : DIAL_CODES

  const selected = DIAL_CODES.find((d) => d.code === value)

  return (
    <div className="dial-code-select" ref={ref}>
      <button
        type="button"
        className="dial-code-select__trigger"
        onClick={() => setOpen(!open)}
      >
        <span className="dial-code-select__flag">{selected?.flag}</span>
        <span className="dial-code-select__code">{value}</span>
        <svg className="dial-code-select__chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5.5 7.5L10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div className="dial-code-select__menu">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search country\u2026"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dial-code-select__search"
          />
          <div className="dial-code-select__list">
            {filtered.map((d) => (
              <button
                key={d.code}
                type="button"
                className={`dial-code-select__option${d.code === value ? ' dial-code-select__option--active' : ''}`}
                onClick={() => {
                  onChange(d.code)
                  setOpen(false)
                  setSearch('')
                }}
              >
                <span>{d.flag}</span>
                <span className="dial-code-select__option-name">{d.country}</span>
                <span className="dial-code-select__option-code">{d.code}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="dial-code-select__empty">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function MarketCurrencySelect({
  value,
  options,
  onChange,
  className = '',
}: {
  value: MarketId
  options: MarketProfile[]
  onChange: (marketId: MarketId) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    setTimeout(() => searchRef.current?.focus(), 50)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filteredOptions = options.filter((entry) => {
    if (!search) return true
    const q = search.toLowerCase()
    return entry.label.toLowerCase().includes(q) || entry.currency.toLowerCase().includes(q)
  })

  const selected = getMarketProfile(value, options)

  return (
    <div className={`market-select ${className}`.trim()} ref={ref}>
      <button
        type="button"
        className="market-select__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Select country"
      >
        <span>{selected.flag}</span>
        <span>{selected.label}</span>
        <svg className="market-select__chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5.5 7.5L10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div className="market-select__menu">
          <input
            ref={searchRef}
            className="market-select__search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search country or currency..."
          />
          <div className="market-select__list">
            {filteredOptions.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`market-select__option${entry.id === value ? ' market-select__option--active' : ''}`}
                onClick={() => {
                  onChange(entry.id)
                  setOpen(false)
                  setSearch('')
                }}
              >
                <span>{entry.flag}</span>
                <span className="market-select__option-name">{entry.label}</span>
                <span className="market-select__option-currency">{entry.currency}</span>
              </button>
            ))}
            {filteredOptions.length === 0 && <p className="dial-code-select__empty">No results</p>}
          </div>
        </div>
      )}
    </div>
  )
}

const SignupContext = createContext<{ openSignup: (type: string, intent?: SignupIntent) => void }>({
  openSignup: () => { },
})

type ModalStep = 'email' | 'details' | 'submitting' | 'success'
type SignupIntent = 'download' | 'demo'
type SignupStringField = Exclude<keyof SignupForm, 'demo_goal'>
type DemoSlotField = 'demo_slot_1' | 'demo_slot_2' | 'demo_slot_3'
const DEMO_SLOT_FIELDS: DemoSlotField[] = ['demo_slot_1', 'demo_slot_2', 'demo_slot_3']

function inferDemoTimeline(demoAvailability: string[]): string | undefined {
  if (demoAvailability.length === 0) return undefined

  const earliest = demoAvailability
    .map((value) => new Date(value))
    .filter((date) => Number.isFinite(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())[0]

  if (!earliest) return undefined

  const now = Date.now()
  const diffDays = Math.floor((earliest.getTime() - now) / (1000 * 60 * 60 * 24))
  if (diffDays <= 7) return 'within_7_days'
  if (diffDays <= 14) return 'within_14_days'
  return 'this_month'
}

interface SignupRequest {
  type: string
  intent: SignupIntent
}

function SignupModal({ isOpen, request, onClose, integrationTiles }: { isOpen: boolean; request: SignupRequest; onClose: () => void; integrationTiles: { key: string; name: string }[] }) {
  const [step, setStep] = useState<ModalStep>('email')
  const [form, setForm] = useState<SignupForm>(EMPTY_FORM)
  const [demoSlotCount, setDemoSlotCount] = useState(1)
  const [errors, setErrors] = useState<Partial<Record<keyof SignupForm, string>>>({})
  const [signupToken, setSignupToken] = useState<string | null>(null)
  const [emailSendFailed, setEmailSendFailed] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  // Escape key + body scroll lock
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // Auto-focus per step
  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => {
      if (step === 'email') emailRef.current?.focus()
      if (step === 'details') nameRef.current?.focus()
    }, 80)
    return () => clearTimeout(timer)
  }, [isOpen, step])

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('email')
      setErrors({})
      setDemoSlotCount(1)
    } else {
      setForm(EMPTY_FORM)
      setErrors({})
      setSignupToken(null)
      setDemoSlotCount(1)
    }
  }, [isOpen])


  if (!isOpen) return null

  const update = (field: SignupStringField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const toggleDemoGoal = (goal: string) => {
    setForm((prev) => {
      const nextGoals = prev.demo_goal.includes(goal)
        ? prev.demo_goal.filter((value) => value !== goal)
        : [...prev.demo_goal, goal]
      return { ...prev, demo_goal: nextGoals }
    })
  }

  const getDemoAvailability = (): string[] =>
    DEMO_SLOT_FIELDS.map((field) => form[field])
      .map((value) => value.trim())
      .filter((value) => value.length > 0)

  const setDemoSlots = (slots: string[]) => {
    setForm((prev) => ({
      ...prev,
      demo_slot_1: slots[0] ?? '',
      demo_slot_2: slots[1] ?? '',
      demo_slot_3: slots[2] ?? '',
    }))
  }

  const addDemoSlot = () => {
    setDemoSlotCount((count) => Math.min(DEMO_MAX_SLOTS, count + 1))
  }

  const removeDemoSlot = (index: number) => {
    const current = DEMO_SLOT_FIELDS.map((field) => form[field]).slice(0, demoSlotCount)
    current.splice(index, 1)
    while (current.length < DEMO_MAX_SLOTS) {
      current.push('')
    }
    setDemoSlots(current)
    setDemoSlotCount((count) => Math.max(1, count - 1))
  }

  const isDemoRequest = request.intent === 'demo'
  const primaryLabel = isDemoRequest ? 'Request demo' : 'Join waitlist'
  const requestStartedEvent = isDemoRequest ? 'demo_request_started' : 'request_started'
  const requestCompletedEvent = isDemoRequest ? 'demo_request_completed' : 'request_completed'

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!EMAIL_RE.test(form.email)) {
      setErrors({ email: 'Enter a valid email address' })
      return
    }
    setErrors({})
    trackEvent(null, requestStartedEvent, { email: form.email, download_type: request.type, intent: request.intent })
    setStep('details')
  }

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Partial<Record<keyof SignupForm, string>> = {}
    const demoAvailability = getDemoAvailability()
    const inferredDemoTimeline = inferDemoTimeline(demoAvailability)
    const demoGoal = form.demo_goal.join(' | ')
    if (!form.name.trim()) newErrors.name = 'Name is required'
    if (form.phone && !PHONE_RE.test(form.phone)) newErrors.phone = 'Enter a valid phone number'
    if (!form.referral) newErrors.referral = 'Select how you heard about us'
    if (isDemoRequest && demoAvailability.length === 0) {
      newErrors.demo_slot_1 = 'Choose at least one preferred date and time'
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors)
      return
    }

    setStep('submitting')
    const row = await submitWaitlist({
      email: form.email,
      name: form.name,
      phone: form.phone ? `${form.dial_code} ${form.phone}` : undefined,
      branches: form.branches,
      referral: form.referral,
      pharmacy_name: form.pharmacy_name || undefined,
      role: form.role || undefined,
      current_system: form.current_system === 'other' && form.current_system_other.trim()
        ? `other:${form.current_system_other.trim()}`
        : form.current_system || undefined,
      download_type: request.type,
      intent: request.intent,
      demo_status: isDemoRequest ? 'requested' : undefined,
      demo_goal: isDemoRequest && demoGoal ? demoGoal : undefined,
      demo_timeline: isDemoRequest ? inferredDemoTimeline : undefined,
      demo_availability: isDemoRequest && demoAvailability.length > 0 ? demoAvailability : undefined,
      demo_timezone: isDemoRequest ? getBrowserTimezone() : undefined,
    })

    if (row) {
      const enrichedRow = {
        ...row,
        intent: request.intent,
        demo_status: isDemoRequest ? 'requested' : row.demo_status,
        demo_goal: isDemoRequest && demoGoal ? demoGoal : row.demo_goal,
        demo_timeline: isDemoRequest ? inferredDemoTimeline : row.demo_timeline,
        demo_availability: isDemoRequest && demoAvailability.length > 0 ? demoAvailability : row.demo_availability,
        demo_timezone: isDemoRequest ? getBrowserTimezone() : row.demo_timezone,
      }
      setSignupToken(enrichedRow.token)
      setStep('success')
      // Fire-and-forget: don't block the success screen on email delivery
      trackEvent(row.token, requestCompletedEvent, {
        download_type: request.type,
        intent: request.intent,
        demo_goal: isDemoRequest && demoGoal ? demoGoal : undefined,
        demo_timeline: isDemoRequest ? inferredDemoTimeline : undefined,
        demo_availability: isDemoRequest && demoAvailability.length > 0 ? demoAvailability : undefined,
        demo_timezone: isDemoRequest ? getBrowserTimezone() : undefined,
      }).catch(() => {})
      sendWaitlistEmail(enrichedRow)
        .then((ok) => { if (!ok) setEmailSendFailed(true) })
        .catch(() => setEmailSendFailed(true))
    } else {
      setStep('details')
      setErrors({ name: 'Something went wrong. Please try again.' })
    }
  }

  const handleSuccessClose = () => {
    if (isDemoRequest) {
      trackEvent(signupToken, 'demo_request_acknowledged', { download_type: request.type, intent: request.intent })
      return
    }
    trackEvent(signupToken, 'waitlist_acknowledged', { download_type: request.type, intent: request.intent })
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        {/* ── Step 1: Email ── */}
        {step === 'email' && (
          <>
            <div className="modal__header">
              <h2>{isDemoRequest ? 'Request a live Fey demo' : 'Get started with Fey'}</h2>
              <p className="modal__sub">{isDemoRequest ? 'Tell us about your workflow and we will schedule a walkthrough.' : 'Get early access to procurement workflows built for pharmacy teams.'}</p>
            </div>
            <form className="modal__form" onSubmit={handleEmailSubmit}>
              <div className="modal__field">
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
                {errors.email && <span className="modal__error">{errors.email}</span>}
              </div>
              <button type="submit" className="btn btn--primary modal__submit">
                Continue
              </button>
            </form>
            <p className="modal__fine">{isDemoRequest ? 'We usually respond in less than one business day.' : 'No credit card required.'}</p>
          </>
        )}

        {/* ── Step 2: Details ── */}
        {(step === 'details' || step === 'submitting') && (
          <>
            <div className="modal__header">
              <h2>A few more details</h2>
              <p className="modal__sub">{request.type} &mdash; we&rsquo;ll send {isDemoRequest ? 'scheduling details' : 'access details'} to <strong>{form.email}</strong></p>
            </div>
            <form className="modal__form" onSubmit={handleDetailsSubmit}>
              <div className="modal__field">
                <input
                  ref={nameRef}
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                />
                {errors.name && <span className="modal__error">{errors.name}</span>}
              </div>
              <div className="modal__field">
                <input
                  type="text"
                  placeholder="Pharmacy name"
                  value={form.pharmacy_name}
                  onChange={(e) => update('pharmacy_name', e.target.value)}
                />
              </div>
              <div className="modal__field">
                <label className="radio-group__label">Your role</label>
                <div className="radio-group">
                  {ROLE_OPTIONS.map((option) => (
                    <label key={option.value} className="radio-group__option">
                      <input
                        type="radio"
                        name="role"
                        value={option.value}
                        checked={form.role === option.value}
                        onChange={(e) => update('role', e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal__field">
                <select value={form.current_system} onChange={(e) => { update('current_system', e.target.value); if (e.target.value !== 'other') update('current_system_other', '') }}>
                  <option value="" disabled>What do you use for procurement today?</option>
                  {integrationTiles.filter((t) => !EXCLUDED_SYSTEM_KEYS.has(t.key)).map((tile) => (
                    <option key={tile.key} value={tile.key}>{tile.name}</option>
                  ))}
                  {NON_SOFTWARE_SYSTEMS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {form.current_system === 'other' && (
                  <input
                    type="text"
                    placeholder="Name of your current system"
                    value={form.current_system_other}
                    onChange={(e) => update('current_system_other', e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
              <div className="modal__field">
                <div className="phone-input-group">
                  <DialCodeSelect
                    value={form.dial_code}
                    onChange={(code) => update('dial_code', code)}
                  />
                  <input
                    type="tel"
                    placeholder="Phone number (optional)"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    className="phone-input-group__input"
                  />
                </div>
                {errors.phone && <span className="modal__error">{errors.phone}</span>}
              </div>
              <div className="modal__field">
                <div className="branches-combo__top">
                  <label htmlFor="branches-slider" className="branches-combo__label">
                    {isDemoRequest ? 'Branches to cover in demo' : 'Number of branches'}
                  </label>
                  <div className="branches-combo__display">
                    <input
                      id="branches-input"
                      type="number"
                      min={String(BRANCH_MIN)}
                      max={String(BRANCH_INPUT_MAX)}
                      value={form.branches}
                      onChange={(e) => {
                        update('branches', normalizeBranchCount(e.target.value, BRANCH_INPUT_MAX))
                      }}
                      className="branches-combo__input"
                    />
                    <span className="branches-combo__unit">
                      {getBranchCount(form.branches) === 1 ? 'branch' : 'branches'}
                    </span>
                  </div>
                </div>
                <div className="branches-combo">
                  <input
                    id="branches-slider"
                    type="range"
                    min={String(BRANCH_MIN)}
                    max={String(BRANCH_SLIDER_MAX)}
                    value={Math.min(BRANCH_SLIDER_MAX, getBranchCount(form.branches))}
                    onChange={(e) => update('branches', normalizeBranchCount(e.target.value, BRANCH_SLIDER_MAX))}
                    className="branches-combo__slider"
                  />
                </div>
              </div>
              {isDemoRequest ? (
                <>
                  <div className="modal__field">
                    <label>What should we focus on?</label>
                    <details className="multi-select">
                      <summary className="multi-select__trigger">
                        {form.demo_goal.length === 0
                          ? 'Select one or more focus areas'
                          : `${form.demo_goal.length} selected`}
                      </summary>
                      <div className="multi-select__menu">
                        {DEMO_FOCUS_OPTIONS.map((option) => (
                          <label key={option.value} className="multi-select__option">
                            <input
                              type="checkbox"
                              checked={form.demo_goal.includes(option.value)}
                              onChange={() => toggleDemoGoal(option.value)}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  <div className="modal__field">
                    <label>Availability (pick up to 3 date/time options)</label>
                    <div className="availability-stack">
                      {Array.from({ length: demoSlotCount }).map((_, index) => {
                        const field = DEMO_SLOT_FIELDS[index]
                        return (
                          <div key={field} className="availability-slot">
                            <input
                              type="datetime-local"
                              value={form[field]}
                              onChange={(e) => update(field, e.target.value)}
                              aria-label={`Availability option ${index + 1}`}
                            />
                            {demoSlotCount > 1 && (
                              <button
                                type="button"
                                className="availability-slot__remove"
                                onClick={() => removeDemoSlot(index)}
                                aria-label={`Remove availability option ${index + 1}`}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className="availability-actions">
                      <button
                        type="button"
                        className="availability-actions__add"
                        onClick={addDemoSlot}
                        disabled={demoSlotCount >= DEMO_MAX_SLOTS}
                      >
                        + Add another date & time
                      </button>
                    </div>
                    {errors.demo_slot_1 && <span className="modal__error">{errors.demo_slot_1}</span>}
                  </div>
                </>
              ) : null}
              <div className="modal__field">
                <select value={form.referral} onChange={(e) => update('referral', e.target.value)}>
                  <option value="" disabled>How did you hear about Fey?</option>
                  <option value="google">Google search</option>
                  <option value="twitter">Twitter / X</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="instagram">Instagram</option>
                  <option value="whatsapp">WhatsApp group</option>
                  <option value="friend">Friend or colleague</option>
                  <option value="pharmacy-association">Pharmacy association</option>
                  <option value="blog">Blog or article</option>
                  <option value="podcast">Podcast</option>
                  <option value="newsletter">Email newsletter</option>
                  <option value="event">Industry event</option>
                  <option value="other">Other</option>
                </select>
                {errors.referral && <span className="modal__error">{errors.referral}</span>}
              </div>
              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--ghost modal__back"
                  onClick={() => { setStep('email'); setErrors({}) }}
                  aria-label="Back"
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="submit"
                  className="btn btn--primary modal__submit"
                  disabled={step === 'submitting'}
                >
                  {step === 'submitting' ? 'Sending\u2026' : primaryLabel}
                </button>
              </div>
            </form>
          </>
        )}

        {/* ── Step 3: Success ── */}
        {step === 'success' && (
          <div className="modal__success">
            {isDemoRequest ? (
              <>
                <div className="modal__success-icon">&#10003;</div>
                <h2>Demo request received</h2>
                <p>We&rsquo;ve received your request and will send scheduling options to <strong>{form.email}</strong>.</p>
              </>
            ) : (
              <>
                <div className="modal__success-icon">&#10003;</div>
                <h2>You&rsquo;re on the list</h2>
                <p>We&rsquo;ll send access details to <strong>{form.email}</strong> shortly.</p>
              </>
            )}
            {emailSendFailed && (
              <p style={{ fontSize: 14, color: '#b45309', marginTop: 8 }}>
                We saved your spot but couldn&rsquo;t send the confirmation email. We&rsquo;ll follow up at <strong>{form.email}</strong> shortly.
              </p>
            )}
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => { handleSuccessClose(); onClose() }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

type RoutePath = '/' | '/pricing' | '/privacy' | '/terms'

const VALID_ROUTES: RoutePath[] = ['/', '/pricing', '/privacy', '/terms']
const BRAND_NAME = 'Fey'

function normalizeRoute(pathname: string): RoutePath {
  const cleaned = pathname.replace(/\/+$/, '') || '/'
  return (VALID_ROUTES.find((route) => route === cleaned) ?? '/') as RoutePath
}

function BrandLockup({
  logoClassName,
  nameClassName,
}: {
  logoClassName: string
  nameClassName: string
}) {
  return (
    <>
      <img src={logoUrl} alt={BRAND_NAME} className={logoClassName} />
      <span className={nameClassName}>{BRAND_NAME}</span>
    </>
  )
}

function DownloadDropdown() {
  const { openSignup } = useContext(SignupContext)
  const detailsRef = useRef<HTMLDetailsElement>(null)

  const pick = (type: string) => {
    if (detailsRef.current) detailsRef.current.open = false
    openSignup(type, 'download')
  }

  return (
    <details className="download-dropdown" ref={detailsRef}>
      <summary className="btn btn--primary download-dropdown__trigger">
        <span>Download Fey</span>
        <svg className="download-dropdown__chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5.5 7.5L10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </summary>
      <div className="download-dropdown__menu">
        <button type="button" className="download-dropdown__option" onClick={() => pick('macOS desktop')}>
          macOS
          <span>For Mac</span>
        </button>
        <button type="button" className="download-dropdown__option" onClick={() => pick('Windows desktop')}>
          Windows
          <span>For PC</span>
        </button>
      </div>
    </details>
  )
}

function MockAppSurface({
  title,
  badge,
  variant = 'default',
  items,
}: {
  title: string
  badge: string
  variant?: 'default' | 'hero' | 'compare'
  items?: string[]
}) {
  return (
    <div className={`mock-app mock-app--${variant}`}>
      <div className="mock-app__chrome">
        <span />
        <span />
        <span />
      </div>
      <div className="mock-app__header">
        <p className="mock-app__title">{title}</p>
        <div className="mock-app__badge">{badge}</div>
      </div>
      <div className="mock-app__body">
        <aside className="mock-app__nav">
          <div className="mock-app__pill" />
          <div className="mock-app__pill mock-app__pill--short" />
          <div className="mock-app__pill" />
        </aside>
        <section className="mock-app__main">
          {items && items.length > 0 ? (
            items.map((item) => (
              <div key={item} className="mock-app__text-row">
                {item}
              </div>
            ))
          ) : (
            <>
              <div className="mock-app__row" />
              <div className="mock-app__row mock-app__row--mid" />
              <div className="mock-app__row" />
              <div className="mock-app__row mock-app__row--short" />
            </>
          )}
        </section>
      </div>
    </div>
  )
}

type DemoScene = {
  id: string
  title: string
  badge: string
  items: string[]
}

function VariantSnippetDemo({
  scenes,
  intervalMs = 9000,
  panelClassName = '',
}: {
  scenes: DemoScene[]
  intervalMs?: number
  panelClassName?: string
}) {
  const [activeSceneIndex, setActiveSceneIndex] = useState(0)

  useEffect(() => {
    if (scenes.length <= 1) return
    const timerId = window.setInterval(() => {
      setActiveSceneIndex((current) => (current + 1) % scenes.length)
    }, intervalMs)
    return () => window.clearInterval(timerId)
  }, [intervalMs, scenes.length])

  const activeScene = scenes[activeSceneIndex]

  return (
    <div className={`variant-media-bg ${panelClassName}`.trim()}>
      <MockAppSurface title={activeScene.title} badge={activeScene.badge} variant="hero" items={activeScene.items} />
    </div>
  )
}

function DemoCtas({ className = '' }: { className?: string }) {
  const { openSignup } = useContext(SignupContext)
  return (
    <div className={className}>
      <DownloadDropdown />
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => openSignup('Guided product demo', 'demo')}
      >
        Request Demo
      </button>
    </div>
  )
}

function HeroCtas({ className = '' }: { className?: string }) {
  const { openSignup } = useContext(SignupContext)
  return (
    <div className={className}>
      <button
        type="button"
        className="btn btn--primary"
        onClick={() => openSignup('Guided product demo', 'demo')}
      >
        Book demo
      </button>
      <a className="btn btn--ghost" href="#workflow">
        See how Fey works
      </a>
    </div>
  )
}

interface HeaderProps {
  route: RoutePath
  onNavigate: (path: RoutePath) => void
}

function VariantHeader({ route, onNavigate }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)

  const handleNavigate = (path: RoutePath) => {
    onNavigate(path)
    setMenuOpen(false)
  }

  return (
    <header className="variant-topbar">
      <a
        href="/"
        onClick={(event) => {
          event.preventDefault()
          handleNavigate('/')
        }}
        className="variant-topbar__brand"
      >
        <BrandLockup logoClassName="variant-topbar__logo" nameClassName="variant-topbar__name" />
      </a>

      <div className="variant-topbar__right">
        <nav className={`variant-topbar__nav variant-topbar__nav--dropdown${menuOpen ? ' variant-topbar__nav--open' : ''}`} aria-label="Primary">
          <a
            href="/pricing"
            className={`variant-topbar__link${route === '/pricing' ? ' variant-topbar__link--active' : ''}`}
            onClick={(event) => {
              event.preventDefault()
              handleNavigate('/pricing')
            }}
          >
            Pricing
          </a>
        </nav>
        <DownloadDropdown />
        <button
          type="button"
          className={`hamburger variant-hamburger${menuOpen ? ' hamburger--open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  )
}

function AppHeader({
  route,
  onNavigate,
}: {
  route: RoutePath
  onNavigate: (path: RoutePath) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header className="nav">
      <a
        className="nav__brand"
        href="/"
        onClick={(event) => {
          event.preventDefault()
          onNavigate('/')
        }}
      >
        <BrandLockup logoClassName="nav__logo" nameClassName="nav__name" />
      </a>
      <button
        type="button"
        className={`hamburger${menuOpen ? ' hamburger--open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
        aria-expanded={menuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <nav className={`nav__menu${menuOpen ? ' nav__menu--open' : ''}`} aria-label="Primary">
        <a
          className={`nav__link${route === '/pricing' ? ' nav__link--active' : ''}`}
          href="/pricing"
          onClick={(event) => {
            event.preventDefault()
            onNavigate('/pricing')
            setMenuOpen(false)
          }}
        >
          Pricing
        </a>
      </nav>
      <div className="nav__cta">
        <DownloadDropdown />
      </div>
    </header>
  )
}

type IntegrationBadgeStatus = 'live' | 'beta' | 'soon'

type IntegrationTile = {
  key: string
  name: string
  abbr: string
  icon: string
  color: string
  status: IntegrationBadgeStatus
}

const INTEGRATION_ICONS: Record<string, string> = {
  dispensary: '💊',
  csv: '📄',
  api: '🔌',
  pms: '💻',
  google_sheets: '📊',
  excel_online: '📗',
  msupply: '🏥',
  whatsapp: '💬',
  quickbooks: '📒',
  xero: '📘',
  sap: '🏭',
  mckesson: '🏪',
  rxworks: '⚕️',
  pioneerrx: '🧪',
  cerner: '🔬',
  fred_dispense: '💉',
  minfos: '📋',
  qs1: '🗄️',
  liberty_software: '🗂️',
  computerrx: '🖥️',
}

function integrationStatusToBadge(status: string): IntegrationBadgeStatus {
  if (status === 'live') return 'live'
  if (status === 'beta') return 'beta'
  return 'soon'
}

function toAbbreviation(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length === 0) return 'IN'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

function colorFromKey(key: string): string {
  let hash = 0
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 70%, 42%)`
}

// PPP markets, FX rates, and market profiles are imported from @fey/core via ./pricing.
// Local aliases for backward compatibility within this file.
const FALLBACK_USD_RATES = CORE_FALLBACK_FX_RATES
const DEFAULT_MARKETS = PPP_MARKETS
const DEFAULT_MARKET_ID: MarketId = CORE_DEFAULT_MARKET_ID

type DisplayCurrency = string
type MarketId = string



interface PricingSummary {
  currency: DisplayCurrency
  marketId: MarketId
  monthlySavings: number
  annualSavings: number
  savingsPercent: number
}

// Comparison rows are imported from core via COMPARISON_ROWS alias.
const PRICING_COMPARISON_ROWS = COMPARISON_ROWS

function resolveMarketFactor(countryCode: string, _currencyCode: string): number {
  const market = PPP_MARKETS.find((m: MarketProfile) => m.id === countryCode)
  return market?.pppFactor ?? 1
}

function countryCodeToFlag(countryCode: string): string {
  if (!/^[A-Z]{2}$/.test(countryCode)) return '\u{1F310}'
  return [...countryCode]
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('')
}

async function fetchFxRates(): Promise<Record<string, number>> {
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/USD', { method: 'GET' })
    if (!response.ok) return FALLBACK_USD_RATES
    const data = (await response.json()) as { rates?: Record<string, number> }
    if (!data.rates) return FALLBACK_USD_RATES
    return { ...FALLBACK_USD_RATES, ...data.rates, USD: 1 }
  } catch {
    return FALLBACK_USD_RATES
  }
}

async function fetchAllMarkets(): Promise<MarketProfile[]> {
  try {
    const response = await fetch('https://restcountries.com/v3.1/all?fields=cca2,name,currencies,flag', { method: 'GET' })
    if (!response.ok) return DEFAULT_MARKETS
    const data = (await response.json()) as Array<{
      cca2?: string
      name?: { common?: string }
      currencies?: Record<string, unknown>
      flag?: string
    }>

    const markets = data
      .map((entry) => {
        const countryCode = (entry.cca2 ?? '').toUpperCase()
        const label = entry.name?.common?.trim() ?? ''
        const currency = Object.keys(entry.currencies ?? {})[0]?.toUpperCase() ?? ''
        if (!countryCode || countryCode.length !== 2 || !label || !currency || currency.length !== 3) {
          return null
        }
        return {
          id: countryCode,
          label,
          currency,
          pppFactor: resolveMarketFactor(countryCode, currency),
          flag: entry.flag ?? countryCodeToFlag(countryCode),
        } satisfies MarketProfile
      })
      .filter((market): market is MarketProfile => Boolean(market))
      .sort((a, b) => a.label.localeCompare(b.label))

    return markets.length > 0 ? markets : DEFAULT_MARKETS
  } catch {
    return DEFAULT_MARKETS
  }
}

async function detectMarketIdByIp(): Promise<MarketId | null> {
  // 1. Cloudflare trace — unlimited, free, same-origin on Cloudflare Pages
  try {
    const cfRes = await fetch('/cdn-cgi/trace', { method: 'GET' })
    if (cfRes.ok) {
      const text = await cfRes.text()
      const match = /^loc=([A-Z]{2})$/m.exec(text)
      if (match?.[1]) return match[1]
    }
  } catch { /* fall through to backup */ }

  // 2. api.country.is — free, CORS enabled, works from any origin
  try {
    const res = await fetch('https://api.country.is/', { method: 'GET' })
    if (res.ok) {
      const data = (await res.json()) as { country?: string }
      const code = (data.country ?? '').toUpperCase()
      if (code && code.length === 2) return code
    }
  } catch { /* give up */ }

  return null
}

function getMarketProfile(marketId: MarketId, marketOptions: MarketProfile[]): MarketProfile {
  return coreGetMarketProfile(marketId, marketOptions)
}

function displayMoney(value: number, currency: DisplayCurrency): string {
  return formatMoney(value, currency)
}

function parseIntField(raw: string, fallback = 0): number {
  const parsed = Number.parseInt(raw.replace(/[^\d]/g, ''), 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

function formatInteger(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('en-US')
}

function getLocalBranchPrice(usdRate: number, market: MarketProfile, fxRates: Record<string, number>) {
  const localPrice = usdRate === TIERS.standard.baseUsdRate
    ? getLocalizedBranchPrice(market, fxRates)
    : usdToLocal(applyPpp(usdRate, market.pppFactor), market.currency, fxRates)
  return { usdRate, localPrice }
}

// Scale slider ranges based on FX rate so amounts feel natural per region.
// Base ranges are calibrated for NGN (rate ~1550). A USD user gets proportionally smaller ranges.
function scaleRange(baseMax: number, baseStep: number, fxRate: number): { max: number; step: number } {
  // NGN baseline rate ~1550. Scale proportionally.
  const scale = Math.max(0.01, fxRate / 1550)
  const max = Math.max(baseStep * 10, Math.round(baseMax * scale / baseStep) * baseStep)
  return { max, step: Math.max(1, Math.round(baseStep * scale / 100) * 100 || baseStep) }
}

function PricingCalculatorForm({
  compact = false,
  onOpenPricing,
  marketOptions,
  marketId,
  onMarketIdChange,
  fxRates,
  calculatorClassName = '',
  onMetricsChange,
  pricingLinkHref,
  onPricingLinkClick,
}: {
  compact?: boolean
  onOpenPricing?: () => void
  marketOptions: MarketProfile[]
  marketId?: MarketId
  onMarketIdChange?: (marketId: MarketId) => void
  fxRates: Record<string, number>
  calculatorClassName?: string
  onMetricsChange?: (summary: PricingSummary) => void
  pricingLinkHref?: string
  onPricingLinkClick?: () => void
}) {
  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_CALCULATOR_INPUTS)

  const resolvedMarketId = marketId ?? marketOptions[0]?.id ?? DEFAULT_MARKET_ID
  const setResolvedMarketId = onMarketIdChange ?? (() => { })
  const market = getMarketProfile(resolvedMarketId, marketOptions)
  const currency = market.currency
  const fxRate = fxRates[currency] ?? 1

  const savings = calculateSavings(inputs)
  const subscription = calculateSubscription(inputs.branches)
  const localBranchPrice = getLocalizedBranchPrice(market, fxRates)
  const localMonthlyPrice = subscription.isCustomPricing
    ? null
    : localBranchPrice * inputs.branches
  const netMonthlySavings = localMonthlyPrice === null
    ? savings.totalMonthlySavings
    : Math.max(0, savings.totalMonthlySavings - localMonthlyPrice)

  const savingsPercent = inputs.monthlyDrugSpend > 0
    ? (savings.totalMonthlySavings / inputs.monthlyDrugSpend) * 100
    : 0

  // Re-scale default inputs when market changes
  const prevMarketRef = useRef(resolvedMarketId)
  useEffect(() => {
    if (prevMarketRef.current !== resolvedMarketId) {
      const prevRate = fxRates[getMarketProfile(prevMarketRef.current, marketOptions).currency] ?? 1
      const nextRate = fxRates[currency] ?? 1
      if (prevRate > 0 && nextRate > 0) {
        const ratio = nextRate / prevRate
        setInputs((prev: CalculatorInputs) => ({
          ...prev,
          monthlyDrugSpend: Math.round(prev.monthlyDrugSpend * ratio),
          monthlyExpiryLoss: Math.round(prev.monthlyExpiryLoss * ratio),
          monthlyStockoutLoss: Math.round(prev.monthlyStockoutLoss * ratio),
          procurementStaffMonthlySalary: Math.round(prev.procurementStaffMonthlySalary * ratio),
        }))
      }
      prevMarketRef.current = resolvedMarketId
    }
  }, [resolvedMarketId, currency, fxRates, marketOptions])

  useEffect(() => {
    if (!onMetricsChange) return
    onMetricsChange({
      currency,
      marketId: resolvedMarketId,
      monthlySavings: savings.totalMonthlySavings,
      annualSavings: savings.totalMonthlySavings * 12,
      savingsPercent,
    })
  }, [currency, onMetricsChange, resolvedMarketId, savings.totalMonthlySavings, savingsPercent])

  const setInputNumber = (field: keyof CalculatorInputs, value: string) => {
    const next = parseIntField(value, 0)
    setInputs((prev: CalculatorInputs) => ({
      ...prev,
      [field]: field === 'branches' ? Math.max(1, next) : next,
    }))
  }

  // Slider ranges scaled to market
  const drugSpendRange = scaleRange(200_000_000, 100_000, fxRate)
  const lossRange = scaleRange(20_000_000, 50_000, fxRate)
  const salaryRange = scaleRange(1_000_000, 10_000, fxRate)

  // Savings line items for side-by-side display
  const savingsLines = [
    { label: 'Drug spend optimisation', today: inputs.monthlyDrugSpend, saved: savings.drugSpendSavings, rate: SAVINGS_RATES.drugSpendOptimisation },
    { label: 'Expiry losses', today: inputs.monthlyExpiryLoss, saved: savings.expirySavings, rate: SAVINGS_RATES.expiryReduction },
    { label: 'Stockout losses', today: inputs.monthlyStockoutLoss, saved: savings.stockoutRecovery, rate: SAVINGS_RATES.stockoutRecovery },
    { label: 'Procurement labor', today: inputs.procurementStaffMonthlySalary, saved: savings.laborSavings, rate: SAVINGS_RATES.laborEfficiency },
    { label: 'Supplier overhead', today: inputs.supplierCount * 15_000, saved: savings.supplierMgmtSavings, rate: SAVINGS_RATES.supplierOverheadPerSupplier },
  ]

  return (
    <section className={`pricing-calculator${compact ? ' pricing-calculator--compact' : ''} ${calculatorClassName}`.trim()}>
      <div className="calculator-grid calculator-grid--two">
        <div className="calculator-card">
          <h4>Your numbers</h4>
          <div className="calculator-fields">
            <div className="calculator-field">
              <div className="calculator-field__meta">
                <span>Branches</span>
              </div>
              <div className="calculator-field__controls">
                <input type="range" min="1" max="200" step="1" value={inputs.branches} onChange={(e) => setInputNumber('branches', e.target.value)} className="calculator-range" />
                <input type="text" value={formatInteger(inputs.branches)} onChange={(e) => setInputNumber('branches', e.target.value)} />
              </div>
            </div>

            <div className="calculator-field">
              <div className="calculator-field__meta">
                <span>Monthly drug spend</span>
              </div>
              <div className="calculator-field__controls">
                <input type="range" min={drugSpendRange.step} max={drugSpendRange.max} step={drugSpendRange.step} value={inputs.monthlyDrugSpend} onChange={(e) => setInputNumber('monthlyDrugSpend', e.target.value)} className="calculator-range" />
                <div className="calculator-money-input">
                  <span>{currency}</span>
                  <input type="text" value={formatInteger(inputs.monthlyDrugSpend)} onChange={(e) => setInputNumber('monthlyDrugSpend', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="calculator-field">
              <div className="calculator-field__meta">
                <span>Monthly expiry losses</span>
              </div>
              <div className="calculator-field__controls">
                <input type="range" min="0" max={lossRange.max} step={lossRange.step} value={inputs.monthlyExpiryLoss} onChange={(e) => setInputNumber('monthlyExpiryLoss', e.target.value)} className="calculator-range" />
                <div className="calculator-money-input">
                  <span>{currency}</span>
                  <input type="text" value={formatInteger(inputs.monthlyExpiryLoss)} onChange={(e) => setInputNumber('monthlyExpiryLoss', e.target.value)} />
                </div>
              </div>
            </div>

            {!compact && (
              <>
                <div className="calculator-field">
                  <div className="calculator-field__meta">
                    <span>Monthly stockout losses</span>
                  </div>
                  <div className="calculator-field__controls">
                    <input type="range" min="0" max={lossRange.max} step={lossRange.step} value={inputs.monthlyStockoutLoss} onChange={(e) => setInputNumber('monthlyStockoutLoss', e.target.value)} className="calculator-range" />
                    <div className="calculator-money-input">
                      <span>{currency}</span>
                      <input type="text" value={formatInteger(inputs.monthlyStockoutLoss)} onChange={(e) => setInputNumber('monthlyStockoutLoss', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="calculator-field">
                  <div className="calculator-field__meta">
                    <span>Procurement staff salary / month</span>
                  </div>
                  <div className="calculator-field__controls">
                    <input type="range" min="0" max={salaryRange.max} step={salaryRange.step} value={inputs.procurementStaffMonthlySalary} onChange={(e) => setInputNumber('procurementStaffMonthlySalary', e.target.value)} className="calculator-range" />
                    <div className="calculator-money-input">
                      <span>{currency}</span>
                      <input type="text" value={formatInteger(inputs.procurementStaffMonthlySalary)} onChange={(e) => setInputNumber('procurementStaffMonthlySalary', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="calculator-field">
                  <div className="calculator-field__meta">
                    <span>Hours / week on procurement</span>
                  </div>
                  <div className="calculator-field__controls">
                    <input type="range" min="1" max="60" step="1" value={inputs.weeklyProcurementHours} onChange={(e) => setInputNumber('weeklyProcurementHours', e.target.value)} className="calculator-range" />
                    <input type="text" value={formatInteger(inputs.weeklyProcurementHours)} onChange={(e) => setInputNumber('weeklyProcurementHours', e.target.value)} />
                  </div>
                </div>

                <div className="calculator-field">
                  <div className="calculator-field__meta">
                    <span>Number of suppliers managed</span>
                  </div>
                  <div className="calculator-field__controls">
                    <input type="range" min="1" max="30" step="1" value={inputs.supplierCount} onChange={(e) => setInputNumber('supplierCount', e.target.value)} className="calculator-range" />
                    <input type="text" value={formatInteger(inputs.supplierCount)} onChange={(e) => setInputNumber('supplierCount', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="calculator-card calculator-card--results">
          <div className="calculator-headline">
            <div className="calculator-headline__top">
              <p className="calculator-headline__label">You save</p>
              <MarketCurrencySelect value={resolvedMarketId} options={marketOptions} onChange={setResolvedMarketId} />
            </div>
            <strong style={{ whiteSpace: 'nowrap' }}>{displayMoney(savings.totalMonthlySavings, currency)}</strong>
            <span>per month across {inputs.branches} {inputs.branches === 1 ? 'branch' : 'branches'}</span>
          </div>
          <dl className="metric-list">
            {savingsLines.map((line) => (
              <div key={line.label}>
                <dt>{line.label}</dt>
                <dd className={line.saved > 0 ? 'metric-good' : ''}>
                  {line.saved > 0 ? `${displayMoney(line.saved, currency)} (-${Math.round(line.rate * 100)}%)` : '—'}
                </dd>
              </div>
            ))}
            <div>
              <dt>Fey subscription</dt>
              <dd>
                {localMonthlyPrice === null
                  ? 'Custom pricing'
                  : `${displayMoney(localMonthlyPrice, currency)} / month`}
              </dd>
            </div>
            <div>
              <dt>Net annual savings</dt>
              <dd className="metric-good">
                <strong>
                  {localMonthlyPrice === null
                    ? 'Custom after demo'
                    : displayMoney(netMonthlySavings * 12, currency)}
                </strong>
              </dd>
            </div>
          </dl>
          {compact && onOpenPricing && (
            <button type="button" className="btn btn--ghost pricing-calculator__cta" onClick={onOpenPricing}>
              Open full pricing
            </button>
          )}
          {pricingLinkHref && (
            <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <a
                href={pricingLinkHref}
                className="btn btn--ghost"
                onClick={onPricingLinkClick ? (e) => { e.preventDefault(); onPricingLinkClick() } : undefined}
              >
                See full pricing plans
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function PricingPage({
  onNavigate,
  marketId,
  onMarketIdChange,
  marketOptions,
  fxRates,
}: {
  onNavigate: (path: RoutePath) => void
  marketId: MarketId
  onMarketIdChange: (marketId: MarketId) => void
  marketOptions: MarketProfile[]
  fxRates: Record<string, number>
}) {
  const { openSignup } = useContext(SignupContext)
  const [pricingSummary, setPricingSummary] = useState<PricingSummary | null>(null)
  const market = getMarketProfile(marketId, marketOptions)
  const savingsHeadline = pricingSummary ? `${Math.max(0, pricingSummary.savingsPercent).toFixed(0)}%` : '0%'

  return (
    <main className="variant-main">
      <div className="variant-canvas">
        <div className="variant-row" id="pricing-tiers">
          <section className="variant-card variant-card--pricing-tiers" aria-labelledby="pricing-plans-title">
            <VariantHeader route="/pricing" onNavigate={onNavigate} />
            <div className="variant-stripe" />
            <div className="pricing-layout">
              <div className="pricing-layout__left">
                <h1 id="pricing-plans-title"><span>Fey + your team</span><span>= more saved than spent</span></h1>
                <p className="pricing-layout__custom">
                  For <strong>6+ branches</strong>,{' '}
                  <button type="button" className="pricing-layout__custom-link" onClick={() => openSignup('Custom pricing demo', 'demo')}>
                    talk to us
                  </button>
                  {' '}for custom pricing and discounts.
                </p>
              </div>
              <div className="pricing-layout__right">
                <article className="pricing-card pricing-card--featured">
                  <p className="pricing-card__range">Standard</p>
                  <h3>1-5 branches</h3>

                  {(() => {
                    const standardTier = TIERS.standard
                    if (standardTier.baseUsdRate === null) return null
                    const { localPrice } = getLocalBranchPrice(standardTier.baseUsdRate, market, fxRates)
                    return (
                      <div className="pricing-card__price-row">
                        <p className="pricing-card__price">
                          {displayMoney(localPrice, market.currency)}
                          {' '}
                          <small>/ branch / month</small>
                        </p>
                        <MarketCurrencySelect value={marketId} options={marketOptions} onChange={onMarketIdChange} className="pricing-card__market" />
                      </div>
                    )
                  })()}

                  <ul className="pricing-card__list">
                    {TIERS.standard.features.map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="btn btn--primary pricing-card__cta"
                    onClick={() => openSignup('Book demo', 'demo')}
                  >
                    Book demo
                  </button>
                </article>
              </div>
            </div>

            {/* Add-ons are shown in the comparison table below */}
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="pricing-calculator">
          <section className="variant-card variant-card--calculator" aria-labelledby="pricing-calculator-title">
            <h2 id="pricing-calculator-title">Estimate your monthly savings.</h2>
            <div className="variant-pricing-calculator">
              <PricingCalculatorForm
                marketOptions={marketOptions}
                marketId={marketId}
                onMarketIdChange={onMarketIdChange}
                fxRates={fxRates}
                onMetricsChange={setPricingSummary}
              />
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="pricing-compare">
          <section className="variant-card variant-card--pricing-compare" aria-labelledby="pricing-compare-title">
            <div className="variant-block variant-block--tight">
              <h2 id="pricing-compare-title">Compare standard and custom pricing.</h2>
            </div>
            <div className="pricing-comparison-wrap">
              <table className="pricing-comparison-table" aria-label="Pricing feature comparison">
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">Standard<br /><span className="pricing-comparison-table__price">{displayMoney(getLocalizedBranchPrice(market, fxRates), market.currency)} / branch</span></th>
                    <th scope="col">Custom<br /><span className="pricing-comparison-table__price">Book demo</span></th>
                  </tr>
                </thead>
                <tbody>
                  {PRICING_COMPARISON_ROWS.map((row: typeof PRICING_COMPARISON_ROWS[number]) => {
                    const supplierProLocal = displayMoney(usdToLocal(applyPpp(SUPPLIER_NETWORK_PRO.usdPricePerBranch, market.pppFactor), market.currency, fxRates), market.currency)
                    const resolveCell = (val: string) => val.replace('{supplier_pro_price}', supplierProLocal)
                    return (
                      <tr key={row.feature}>
                        <th scope="row">{row.feature}</th>
                        <td>{resolveCell(row.standard)}</td>
                        <td>{resolveCell(row.custom)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="pricing-faq">
          <section className="variant-card variant-card--faq" aria-labelledby="pricing-faq-title">
            <div className="variant-block">
              <h2 id="pricing-faq-title">Pricing questions</h2>
            </div>
            <div className="variant-faq-list">
              <details className="faq-item">
                <summary>How does regional pricing work?</summary>
                <p>Prices are shown in your local currency based on your location. Priority markets use reviewed local prices, and unsupported markets use a PPP-informed fallback before final billing is confirmed.</p>
              </details>
              <details className="faq-item">
                <summary>What is Automated Procurement?</summary>
                <p>When enabled, Fey places orders autonomously based on your rules and thresholds. You pay 1% of the procurement spend that flows through Fey — no fixed add-on fee. It includes everything in Supplier Network Pro.</p>
              </details>
              <details className="faq-item">
                <summary>What happens after five branches?</summary>
                <p>Teams with more than five branches move to custom pricing so rollout, integrations, approvals, and support can match the network.</p>
              </details>
              <details className="faq-item">
                <summary>Do you support annual contracts?</summary>
                <p>Yes. Annual billing and rollout support are available during onboarding, especially for custom multi-branch deployments.</p>
              </details>
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row">
          <section className="variant-card variant-card--closing">
            <div className="variant-closing">
              <div className="variant-closing__copy">
                <h2>Save {savingsHeadline} on your next procurement cycle.</h2>
              </div>
              <DemoCtas className="variant-closing__actions" />
            </div>
          </section>
        </div>
      </div>
    </main>
  )
}

function LandingVariantPage({
  onNavigate,
  marketId,
  onMarketIdChange,
  marketOptions,
  fxRates,
  integrationTiles,
}: {
  onNavigate: (path: RoutePath) => void
  marketId: MarketId
  onMarketIdChange: (marketId: MarketId) => void
  marketOptions: MarketProfile[]
  fxRates: Record<string, number>
  integrationTiles: IntegrationTile[]
}) {
  const heroScenes: DemoScene[] = [
    {
      id: 'summary',
      title: 'Purchase plan',
      badge: 'Live today',
      items: ['What to buy: 24 priority items', 'When to reorder: next 7 days', 'Who to source from: 3 supplier options'],
    },
    {
      id: 'systems',
      title: 'Stock movement',
      badge: 'Step 1',
      items: ['Fast movers flagged', 'Slow movers held back', 'Expiry risk included'],
    },
    {
      id: 'quotes',
      title: 'Supplier options',
      badge: 'Step 3',
      items: ['Availability checked', 'Price variance surfaced', 'Best option ready for approval'],
    },
  ]

  const workflowScenes: DemoScene[] = [
    {
      id: 'connect',
      title: 'Connect inventory',
      badge: 'Step 1',
      items: ['Source: Dispensary POS', 'Source: CSV upload', 'Sync status: Healthy'],
    },
    {
      id: 'suggest',
      title: 'Review suggestions',
      badge: 'Step 2',
      items: ['Amoxicillin: 7-day expiry risk', 'Paracetamol: demand spike +34%', 'Cetirizine: low stock in Branch 2'],
    },
    {
      id: 'request',
      title: 'Request quotes',
      badge: 'Step 3',
      items: ['RFQ sent to 3 suppliers', 'First response in 14 minutes', 'Lead time winner: 2 days'],
    },
  ]

  const productPanels = [
    {
      id: 'buying-decisions',
      title: 'Buy the right stock.',
      artifactTitle: 'Decision receipt',
      status: 'Recommended',
      signal: 'Paracetamol demand is up 34% across the last 7 days.',
      recommendation: 'Add 18 packs to the next purchase list.',
      reason: 'Current stock covers 5 days. Supplier lead time is 2 days.',
      action: 'Add to purchase list',
      details: [
        { label: 'Fast mover', value: 'Prioritise' },
        { label: 'Expiry risk', value: 'Reduce quantity' },
        { label: 'Slow stock', value: 'Hold' },
      ],
    },
    {
      id: 'reorder-automation',
      title: 'Reorder before gaps.',
      artifactTitle: 'Timing receipt',
      status: 'Needs approval',
      signal: 'Amoxicillin reaches reorder point in 3 days.',
      recommendation: 'Approve the order today.',
      reason: 'The preferred supplier needs 2 days to deliver.',
      action: 'Review quantity',
      details: [
        { label: 'Today', value: 'Approve' },
        { label: '3 days', value: 'Reorder point' },
        { label: '7 days', value: 'Stockout risk' },
      ],
    },
    {
      id: 'supplier-sourcing',
      title: 'Compare supplier prices.',
      artifactTitle: 'Supplier comparison',
      status: 'Best source',
      signal: 'Three suppliers can fill the order today.',
      recommendation: 'Choose the option with the best price, availability, and delivery fit.',
      reason: 'The lowest price is checked against stock availability and delivery timing before approval.',
      action: 'Review supplier prices',
      details: [
        { label: 'Lowest price', value: 'Safco' },
        { label: 'Fastest delivery', value: 'New Heights' },
        { label: 'Best availability', value: 'DrugStoc' },
      ],
    },
    {
      id: 'branch-control',
      title: 'Keep branch buying visible.',
      artifactTitle: 'Exception ledger',
      status: 'Exception flagged',
      signal: 'Branch 2 is short while Main branch has excess stock.',
      recommendation: 'Transfer before placing a new supplier order.',
      reason: 'The transfer covers demand faster and avoids extra cash tied in stock.',
      action: 'Route for approval',
      details: [
        { label: 'Branch 2', value: 'Needs stock' },
        { label: 'Main branch', value: 'Can transfer' },
        { label: 'Owner', value: 'Approval pending' },
      ],
    },
    {
      id: 'procurement-intelligence',
      title: 'See how each order affects profit.',
      artifactTitle: 'Order cost check',
      status: 'Updated',
      signal: 'This supplier is cheaper, but delivery takes longer.',
      recommendation: 'Use the lower-cost supplier only if current stock can cover the wait.',
      reason: 'A cheaper order can still hurt profit if it creates a stock gap or ties up cash.',
      action: 'Review order cost',
      details: [
        { label: 'Current stock', value: 'Can wait' },
        { label: 'Supplier price', value: 'Lower' },
        { label: 'Delivery timing', value: 'Longer' },
      ],
    },
  ]

  return (
    <main className="variant-main">
      <div className="variant-canvas">
        <div className="variant-row" id="top">
          <section className="variant-card variant-card--hero" aria-labelledby="variant-hero-title">
            <VariantHeader route="/" onNavigate={onNavigate} />
            <div className="variant-stripe" />
            <div className="variant-hero-copy">
              <h1 id="variant-hero-title">
                <span>AI procurement agent</span>
                <span>for pharmacies</span>
              </h1>
              <p>Fey improves pharmacy margins by tracking stock movement, identifying what to buy, and comparing supplier prices to lower purchasing costs.</p>
              <HeroCtas className="variant-hero-actions" />
            </div>
            <div className="variant-hero-media">
              <VariantSnippetDemo scenes={heroScenes} />
            </div>
            <div className="variant-stripe" />
          </section>

        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="proof">
          <section className="variant-card variant-card--proof" aria-labelledby="variant-proof-title">
            <div className="variant-block">
              <h2 id="variant-proof-title">Built around real pharmacy purchasing work.</h2>
            </div>
            <div className="variant-proof-grid">
              <article>
                <p className="variant-proof-value">1,029</p>
                <h3>Pharmacy partners</h3>
              </article>
              <article>
                <p className="variant-proof-value">Same day</p>
                <h3>Average setup window</h3>
              </article>
              <article>
                <p className="variant-proof-value">1 hour</p>
                <h3>Guided walkthrough</h3>
              </article>
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="integrations">
          <section className="variant-card variant-card--integrations" aria-labelledby="variant-integrations-title">
            <div className="variant-block">
              <h2 id="variant-integrations-title">Connect to the systems you already use.</h2>
            </div>
            {integrationTiles.length > 0 ? (
              <div className="integration-carousel">
                <div className="integration-carousel__track">
                  {[...integrationTiles, ...integrationTiles].map((item, i) => (
                    <div key={`${item.name}-${i}`} className="integration-tile">
                      <div
                        className="integration-tile__icon"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon || item.abbr}
                      </div>
                      <span>{item.name}</span>
                      <span className={`integration-badge integration-badge--${item.status}`}>{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="variant-integrations-loading">Integrations are loading.</p>
            )}
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        {productPanels.map((panel, index) => (
          <Fragment key={panel.id}>
            <div className="variant-row" id={panel.id}>
              <section className="variant-card variant-card--product-panel" aria-labelledby={`${panel.id}-title`}>
                <div className="product-panel">
                  <div className="product-panel__copy">
                    <h2 id={`${panel.id}-title`}>{panel.title}</h2>
                  </div>
                  <div className="product-artifact" aria-label={`${panel.title} decision receipt`}>
                    <div className="product-artifact__top">
                      <span>{panel.artifactTitle}</span>
                      <strong>{panel.status}</strong>
                    </div>
                    <div className="product-artifact__decision">
                      <span>Signal</span>
                      <h3>{panel.signal}</h3>
                    </div>
                    <div className="product-artifact__reasoning">
                      <div>
                        <span>Recommendation</span>
                        <strong>{panel.recommendation}</strong>
                      </div>
                      <div>
                        <span>Reason</span>
                        <strong>{panel.reason}</strong>
                      </div>
                    </div>
                    <div className="product-artifact__rows">
                      {panel.details.map((detail) => (
                        <div key={detail.label}>
                          <span>{detail.label}</span>
                          <strong>{detail.value}</strong>
                        </div>
                      ))}
                    </div>
                    <div className="product-artifact__action">
                      <span>Next action</span>
                      <strong>{panel.action}</strong>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            {index < productPanels.length - 1 && (
              <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />
            )}
          </Fragment>
        ))}

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="workflow">
          <section className="variant-card variant-card--why" aria-labelledby="variant-workflow-title">
            <div className="variant-split">
              <div>
                <div className="variant-block">
                  <h2 id="variant-workflow-title">From stock data to purchasing decisions.</h2>
                </div>
                <div className="variant-step-list">
                  <article>
                    <span>Step 1</span>
                    <h3>Connect stock movement</h3>
                    <p>Import inventory, expiry, and sales data from the systems your pharmacy already uses.</p>
                  </article>
                  <article>
                    <span>Step 2</span>
                    <h3>Review what to buy</h3>
                    <p>Fey prioritises fast movers, expiry risk, demand changes, and products tying up cash.</p>
                  </article>
                  <article>
                    <span>Step 3</span>
                    <h3>Compare supplier prices</h3>
                    <p>Compare supplier prices, delivery timing, and availability before each order.</p>
                  </article>
                </div>
              </div>
              <div className="variant-split__panel">
                <VariantSnippetDemo scenes={workflowScenes} panelClassName="variant-media-bg--compact" intervalMs={9000} />
              </div>
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="pricing-estimator">
          <section className="variant-card variant-card--calculator" aria-labelledby="variant-pricing-title">
            <div className="variant-block variant-block--tight variant-block--pricing-intro">
              <h2 id="variant-pricing-title">Estimate what you pay and what you save.</h2>
            </div>
            <div className="variant-calculator-wrap">
              <PricingCalculatorForm
                marketOptions={marketOptions}
                marketId={marketId}
                onMarketIdChange={onMarketIdChange}
                fxRates={fxRates}
                compact={false}
                onOpenPricing={() => onNavigate('/pricing')}
                pricingLinkHref="/pricing"
                onPricingLinkClick={() => onNavigate('/pricing')}
              />
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row" id="faq">
          <section className="variant-card variant-card--faq" aria-labelledby="variant-faq-title">
            <div className="variant-block">
              <h2 id="variant-faq-title">Common questions</h2>
            </div>
            <div className="variant-faq-list">
              <details className="faq-item">
                <summary>How does Fey connect to my pharmacy system?</summary>
                <p>Fey supports CSV and Excel imports, Google Sheets sync, and direct integrations with popular pharmacy POS systems including RxWorks, McKesson, and more. Most setups are live in minutes.</p>
              </details>
              <details className="faq-item">
                <summary>How long does setup take?</summary>
                <p>Most pharmacies are up and running in under 15 minutes. Import your inventory, connect your accounts, and Fey starts generating insights immediately.</p>
              </details>
              <details className="faq-item">
                <summary>Is my data secure?</summary>
                <p>Yes. Fey uses encryption in transit and at rest, role-based access controls, and follows industry best practices for data protection. Your pharmacy data is never shared with third parties.</p>
              </details>
              <details className="faq-item">
                <summary>Can I use Fey across multiple branches?</summary>
                <p>Absolutely. Fey is built for multi-branch operations. See stock risks, procurement activity, and supplier performance across every location in one dashboard.</p>
              </details>
              <details className="faq-item">
                <summary>What if my system isn&apos;t listed?</summary>
                <p>We add new integrations regularly. Share your system during the demo and we will confirm the best setup path.</p>
              </details>
              <details className="faq-item">
                <summary>How does the AI generate procurement lists?</summary>
                <p>Fey analyses your stock levels, expiry dates, sales velocity, and supplier history to build optimised procurement lists. You review and approve before any orders go out.</p>
              </details>
            </div>
          </section>
        </div>

        <div className="variant-stripe variant-canvas__divider" aria-hidden="true" />

        <div className="variant-row">
          <section className="variant-card variant-card--closing">
            <div className="variant-closing">
              <div className="variant-closing__copy">
                <h2>Run your next procurement cycle with Fey.</h2>
                <p>Set up your branch in minutes &amp; save on your next procurement.</p>
              </div>
              <DemoCtas className="variant-closing__actions" />
            </div>
          </section>
        </div>
      </div>
    </main>
  )
}

function PrivacyPage() {
  return (
    <main className="main">
      <section className="section section--legal">
        <div className="section__inner legal">
          <div className="section__header">
            <h1>Privacy Policy</h1>
            <p className="section__lede">Effective date: February 23, 2026 | Last updated: February 23, 2026</p>
          </div>
          <article className="legal-block">
            <h2>1. Introduction</h2>
            <p>
              Fey Technologies ("Fey", "we", "our", or "us") provides AI-powered pharmacy procurement software that helps pharmacies
              manage inventory, generate procurement lists, track expiry risks, and communicate with suppliers. This Privacy Policy
              describes how we collect, use, disclose, and protect personal information when you use our services, including our
              website, mobile applications, desktop applications, and API services (collectively, the "Services").
            </p>
            <p>
              <strong>Who We Are.</strong> Fey Technologies is organized under the laws of the State of Delaware, United States,
              with operations in the United States, Nigeria, and Qatar. Our registered address is 256 Chapman Road, Newark,
              Delaware 19702, United States. We also operate from the Qatar Financial Center, Doha, Qatar.
            </p>
            <p>
              <strong>Scope.</strong> This Privacy Policy applies to all users of our Services, including individuals who register
              for accounts ("Users"), administrators who manage organizational accounts ("Administrators"), and individuals whose
              information is processed through our Services ("Data Subjects"). If you are using our Services on behalf of an
              organization, you represent that you have authority to bind that organization to this Privacy Policy.
            </p>
            <p>
              <strong>Data Controller.</strong> For users located in the European Economic Area ("EEA"), United Kingdom, or Switzerland,
              the data controller responsible for your personal data is Fey Technologies, with the contact details specified in
              Section 15. For users located elsewhere, Fey Technologies acts as the data controller for the personal data described
              in this policy.
            </p>
            <p>
              <strong>Data Protection Officer.</strong> We have appointed a Data Protection Officer ("DPO") who is responsible for
              overseeing our privacy practices. You may contact our DPO at <a href="mailto:dpo@fey.supply">dpo@fey.supply</a> for
              any questions about this Privacy Policy or our data processing activities.
            </p>
            <p>
              <strong>EU Representative.</strong> In accordance with Article 27 of the General Data Protection Regulation ("GDPR"),
              we have designated a representative in the European Union for data subjects located in the EEA. You may contact our
              EU representative by emailing <a href="mailto:eu-rep@fey.supply">eu-rep@fey.supply</a>. This representative serves as
              a point of contact for supervisory authorities and data subjects on all issues related to our processing of personal
              data of individuals in the EEA.
            </p>
            <p>
              <strong>Regulatory Framework.</strong> This Privacy Policy is designed to comply with applicable data protection laws
              worldwide, including the EU General Data Protection Regulation (GDPR), the UK Data Protection Act 2018 and UK GDPR,
              the Swiss Federal Act on Data Protection (FADP), the California Consumer Privacy Act and California Privacy Rights Act
              (CCPA/CPRA), the Nigeria Data Protection Regulation 2019 (NDPR) and Nigeria Data Protection Act 2023 (NDPA),
              Qatar's Law No. 13 of 2016 on Personal Data Privacy Protection (PDPPL), and other applicable privacy laws.
              Where local requirements differ, the more protective standard applies.
            </p>
          </article>
          <article className="legal-block">
            <h2>2. Information We Collect</h2>
            <p>We collect and receive information through several sources:</p>
            
            <h3>2.1 Information You Provide</h3>
            <p><strong>Account Information.</strong> When you create an account, we collect your name, email address, phone number,
            password, and organizational details including pharmacy name, branch locations, business address, and pharmacy license
            information. For administrators, we may also collect billing information, tax identification numbers, and payment details.</p>
            
            <p><strong>Pharmacy Data.</strong> To provide our Services, you may upload or connect the following types of data:</p>
            <ul>
              <li>Inventory records including product names, quantities, stock levels, batch numbers, and unit costs</li>
              <li>Expiry dates and shelf-life information for pharmaceutical products</li>
              <li>Supplier information including names, contact details, and pricing agreements</li>
              <li>Purchase history and transaction records</li>
              <li>Procurement lists and purchase orders</li>
              <li>Integration credentials for connecting to pharmacy management systems</li>
            </ul>
            
            <p><strong>Communication Data.</strong> When you use our RFQ (Request for Quote) features or communicate with suppliers
            through our Services, we collect and process supplier contact details, quote requests, responses, and related communications.</p>
            
            <p><strong>Support Communications.</strong> If you contact our customer support team, we collect information you provide
            in those communications, including support tickets, chat transcripts, email correspondence, and any attachments you share.</p>
            
            <p><strong>Marketing Information.</strong> If you subscribe to our marketing communications, we collect the information
            you provide, such as your email address and communication preferences.</p>
            
            <h3>2.2 Information Collected Automatically</h3>
            <p><strong>Usage Information.</strong> When you use our Services, we automatically collect technical information including:</p>
            <ul>
              <li>Log data: IP addresses, browser type and version, operating system, access times, pages viewed, and referring URLs</li>
              <li>Device information: Device type, unique device identifiers, device settings, and crash data</li>
              <li>Service metadata: Features used, actions taken, and navigation patterns within the Services</li>
              <li>Session information: Login and logout times, session duration, and authentication events</li>
            </ul>
            
            <p><strong>Location Information.</strong> We may approximate your location based on your IP address for security purposes,
            fraud prevention, and to provide localized content. We do not collect precise GPS location from our Services.</p>
            
            <p><strong>Cookies and Tracking Technologies.</strong> We use cookies, web beacons, pixel tags, and similar technologies
            to operate and improve our Services. For detailed information, see Section 11 (Cookies and Tracking Technologies).</p>
            
            <h3>2.3 Information from Third Parties</h3>
            <p><strong>Integrations.</strong> When you connect third-party services such as pharmacy management systems, we may receive
            data from those services in accordance with the permissions you grant. This may include inventory data, supplier information,
            and transaction history.</p>
            
            <p><strong>Business Partners.</strong> We may receive information from business partners, suppliers, and service providers
            to improve our Services or verify user information.</p>
            
            <p><strong>Publicly Available Information.</strong> We may collect publicly available information relevant to our Services,
            such as pharmaceutical pricing databases or regulatory information.</p>
            
            <h3>2.4 Categories of Personal Information</h3>
            <p>For purposes of applicable privacy laws, the categories of personal information we collect include:</p>
            <ul>
              <li><strong>Identifiers:</strong> Name, email address, phone number, user ID, account credentials</li>
              <li><strong>Professional Information:</strong> Job title, pharmacy name, pharmacy license number, business address</li>
              <li><strong>Commercial Information:</strong> Transaction history, purchase records, billing information</li>
              <li><strong>Financial Information:</strong> Payment card details (processed by payment processors), billing address</li>
              <li><strong>Internet Activity:</strong> Browsing history, search queries, interaction data within the Services</li>
              <li><strong>Geolocation:</strong> Approximate location derived from IP address</li>
              <li><strong>Sensory Data:</strong> Audio recordings (if you use voice features), images (if you upload product photos)</li>
              <li><strong>Inferences:</strong> Conclusions drawn from other personal information about preferences, behavior, or characteristics</li>
            </ul>
          </article>
          <article className="legal-block">
            <h2>3. How We Use Your Information</h2>
            <p>We use personal information for the following purposes:</p>
            
            <h3>3.1 Service Delivery and Performance</h3>
            <ul>
              <li>Create, manage, and authenticate user accounts</li>
              <li>Sync and manage inventory data across multiple branches</li>
              <li>Generate AI-powered procurement list recommendations</li>
              <li>Provide expiry and stockout risk alerts</li>
              <li>Route RFQ requests to suppliers and facilitate supplier communications</li>
              <li>Enable integrations with pharmacy management systems and other third-party services</li>
              <li>Provide customer support and respond to inquiries</li>
            </ul>
            
            <h3>3.2 AI Features and Processing</h3>
            <p>Our AI features analyze your inventory patterns, expiry risks, and stock movement to generate recommendations.
            We do not use your pharmacy data to train AI models without your explicit consent. When you use AI-powered features:</p>
            <ul>
              <li>Data is processed to provide real-time insights and recommendations</li>
              <li>Processing occurs within your workspace context</li>
              <li>Your data is sent to our AI providers (currently Google AI) under strict data processing agreements that prohibit using your data for model training</li>
              <li>You retain control over AI-generated outputs and can modify or reject suggestions</li>
            </ul>
            
            <h3>3.3 Security and Fraud Prevention</h3>
            <ul>
              <li>Detect, prevent, and address fraud, abuse, and security incidents</li>
              <li>Protect against unauthorized access to accounts and data</li>
              <li>Monitor for suspicious activity and policy violations</li>
              <li>Maintain audit logs and security records</li>
            </ul>
            
            <h3>3.4 Service Improvement and Development</h3>
            <ul>
              <li>Analyze aggregated, anonymized usage patterns to improve our Services</li>
              <li>Develop new features and functionality</li>
              <li>Conduct research and analytics</li>
              <li>Test and debug our Services</li>
            </ul>
            
            <h3>3.5 Communications</h3>
            <ul>
              <li>Send service updates, security alerts, and administrative notices</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Send marketing communications (where you have opted in) about new features, promotions, and news</li>
            </ul>
            
            <h3>3.6 Legal Compliance</h3>
            <ul>
              <li>Comply with applicable laws, regulations, and legal processes</li>
              <li>Respond to lawful requests from public authorities</li>
              <li>Enforce our agreements and protect our rights</li>
              <li>Maintain records for regulatory compliance</li>
            </ul>
            
            <h3>3.7 Legal Bases for Processing (EEA, UK, Switzerland)</h3>
            <p>For users in the European Economic Area, United Kingdom, and Switzerland, we process personal data based on:</p>
            <ul>
              <li><strong>Contract:</strong> Processing necessary to fulfill our contractual obligations and provide our Services, including account creation, service delivery, and customer support</li>
              <li><strong>Legitimate Interest:</strong> Processing for our legitimate interests, including improving our Services, preventing fraud, ensuring security, and communicating with you, where such interests are not overridden by your rights</li>
              <li><strong>Consent:</strong> Processing based on consent you have provided for specific purposes, such as marketing communications or certain AI features</li>
              <li><strong>Legal Obligation:</strong> Processing necessary to comply with applicable laws and regulations</li>
              <li><strong>Vital Interests:</strong> Processing necessary to protect vital interests of individuals</li>
            </ul>
            <p>You may object to processing based on legitimate interests at any time by contacting us at <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.</p>

            <h3>3.8 Data Protection Impact Assessments</h3>
            <p>We conduct Data Protection Impact Assessments ("DPIAs") before implementing new processing activities that are
            likely to result in a high risk to the rights and freedoms of individuals, as required by GDPR Article 35. In particular,
            we have conducted DPIAs for:</p>
            <ul>
              <li>Our AI-powered procurement recommendation engine, which processes pharmacy inventory data to generate automated suggestions</li>
              <li>International data transfers between our operating jurisdictions (United States, Nigeria, Qatar, EEA)</li>
              <li>Our integration framework, which connects to third-party pharmacy management systems and processes pharmacy operational data</li>
              <li>Our proactive monitoring features, which analyze stock patterns to detect and alert on expiry risks and stockout conditions</li>
            </ul>
            <p>DPIAs are reviewed and updated annually, or when there are significant changes to the processing activities they cover.
            Summaries of our DPIAs are available to supervisory authorities upon request. If you have questions about our DPIAs,
            contact our DPO at <a href="mailto:dpo@fey.supply">dpo@fey.supply</a>.</p>
          </article>
          <article className="legal-block">
            <h2>4. How We Share Your Information</h2>
            <p>We share personal information in the following circumstances:</p>
            
            <h3>4.1 Service Providers</h3>
            <p>We engage third-party service providers to perform services on our behalf. These providers process personal information
            only for the purposes we specify and are contractually bound to protect your data through Data Processing Agreements ("DPAs")
            that include confidentiality obligations, data security requirements, breach notification procedures, and restrictions on
            sub-processing. We conduct due diligence on all service providers before engagement and perform periodic reviews of their
            security practices and compliance. Categories of service providers include:</p>
            <ul>
              <li><strong>Cloud Infrastructure and Hosting:</strong> Computing, storage, and content delivery services that host our
              application and databases. Data is encrypted at rest and in transit. Servers are located in regions selected for
              compliance with applicable data residency requirements.</li>
              <li><strong>Database and Data Storage:</strong> Managed database services and file storage for persistent data. We use
              row-level security policies to enforce tenant isolation, ensuring each pharmacy's data is logically separated.</li>
              <li><strong>AI and Machine Learning Processing:</strong> Large language model providers (currently Google AI) used to
              power our AI procurement assistant. Your data is sent to these providers only when you actively use AI features,
              under strict DPAs that prohibit using your data for model training, and data is not retained by the provider
              beyond the processing request.</li>
              <li><strong>Authentication and Identity:</strong> Services that manage user authentication, session management,
              and identity verification, including multi-factor authentication and passwordless login.</li>
              <li><strong>Payment Processing:</strong> PCI DSS-compliant payment processors that handle credit card and billing
              information. We do not store full payment card numbers on our servers; card data is tokenized by the processor.</li>
              <li><strong>Email and Communications:</strong> Transactional email services for account verification, password resets,
              procurement alerts, and digest notifications. Marketing email services for optional newsletters and product updates
              (opt-in only).</li>
              <li><strong>Analytics and Performance Monitoring:</strong> Services that help us understand usage patterns, diagnose
              errors, and monitor application performance. Analytics data is aggregated and does not include pharmacy inventory
              or financial data.</li>
              <li><strong>Customer Support:</strong> Help desk and communication tools used to respond to support inquiries and
              manage support tickets.</li>
              <li><strong>Security and Compliance:</strong> Services for threat detection, vulnerability scanning, penetration
              testing, audit logging, and compliance monitoring.</li>
              <li><strong>Content Delivery:</strong> Content delivery networks (CDNs) that accelerate delivery of static assets
              and improve application performance globally.</li>
            </ul>
            <p>All service providers are bound by contractual obligations that are at least as protective as this Privacy Policy.
            We require our service providers to notify us promptly of any security incidents affecting your data. We do not permit
            service providers to use your personal information for their own purposes or to share it with other parties except as
            necessary to provide services to us.</p>
            
            <h3>4.2 Supplier Communications</h3>
            <p>When you use our RFQ features, we share item details, quantity requirements, and your contact information with your
            chosen suppliers to facilitate procurement communications. You control which suppliers receive this information.</p>
            
            <h3>4.3 Third-Party Integrations</h3>
            <p>When you connect third-party services to our Services, we may share information with those services in accordance
            with the permissions you grant. We are not responsible for the privacy practices of these third-party services.</p>
            
            <h3>4.4 Business Transfers</h3>
            <p>If Fey is involved in a merger, acquisition, reorganization, bankruptcy, or sale of assets, your personal information
            may be transferred as part of that transaction. We will provide notice of any such transfer and your choices regarding your information.</p>
            
            <h3>4.5 Legal Requirements</h3>
            <p>We may disclose personal information when required by law, regulation, legal process, or governmental request, or when
            we believe disclosure is necessary to:</p>
            <ul>
              <li>Comply with legal obligations or respond to lawful requests</li>
              <li>Protect our rights, privacy, safety, or property</li>
              <li>Protect the rights, safety, or property of our users or others</li>
              <li>Enforce our agreements or policies</li>
              <li>Investigate, prevent, or take action regarding illegal activities, fraud, or security incidents</li>
            </ul>
            
            <h3>4.6 With Your Consent</h3>
            <p>We may share personal information with third parties when you provide consent to do so.</p>
            
            <h3>4.7 Aggregated and De-Identified Information</h3>
            <p>We may share aggregated, de-identified, or anonymized information that cannot reasonably be used to identify you
            for any purpose without restriction.</p>
          </article>
          <article className="legal-block">
            <h2>5. Data Retention</h2>
            <p>We retain personal information for as long as necessary to fulfill the purposes described in this Privacy Policy,
            unless a longer retention period is required or permitted by law. The following table provides general retention periods:</p>
            
            <h3>5.1 Retention Periods by Data Category</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Data Category</th>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Account Information</td>
                  <td style={{ padding: '12px 8px' }}>Duration of account plus 90 days, or as required by law</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Pharmacy Data (Inventory, Suppliers)</td>
                  <td style={{ padding: '12px 8px' }}>Duration of account plus 90 days, unless deleted by user</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Transaction Records</td>
                  <td style={{ padding: '12px 8px' }}>7 years from transaction date (tax and regulatory requirements)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Payment Information</td>
                  <td style={{ padding: '12px 8px' }}>Duration of account plus 7 years (financial regulations)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Usage Logs</td>
                  <td style={{ padding: '12px 8px' }}>12 months, then aggregated or deleted</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Security Logs</td>
                  <td style={{ padding: '12px 8px' }}>24 months for active security monitoring</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Marketing Consent Records</td>
                  <td style={{ padding: '12px 8px' }}>Duration of consent plus 3 years (to demonstrate compliance)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Support Communications</td>
                  <td style={{ padding: '12px 8px' }}>3 years from last interaction</td>
                </tr>
              </tbody>
            </table>
            
            <h3>5.2 Deletion Requests</h3>
            <p>When you request deletion of your personal information or delete your account, we will remove your personal information
            from our active systems within 90 days, except where retention is required by law or necessary for legitimate business
            purposes such as security, fraud prevention, or dispute resolution. We will also instruct our service providers to delete
            your personal information in accordance with their data processing agreements.</p>
            
            <h3>5.3 Anonymized Data</h3>
            <p>Information that has been properly anonymized or de-identified in accordance with applicable law is not personal
            information and may be retained indefinitely.</p>
          </article>
          <article className="legal-block">
            <h2>6. Data Security</h2>
            <p>We implement industry-standard technical, administrative, and physical security measures designed to protect
            personal information from unauthorized access, use, disclosure, alteration, and destruction. These measures include:</p>
            
            <h3>6.1 Technical Safeguards</h3>
            <ul>
              <li>Encryption of data in transit using TLS 1.2 or higher</li>
              <li>Encryption of data at rest using AES-256 or equivalent</li>
              <li>Role-based access controls and least-privilege access principles</li>
              <li>Multi-factor authentication for administrative access</li>
              <li>Regular penetration testing and vulnerability assessments</li>
              <li>Automated security monitoring and intrusion detection</li>
              <li>Secure software development practices and code reviews</li>
            </ul>
            
            <h3>6.2 Administrative Safeguards</h3>
            <ul>
              <li>Background checks for employees with access to personal information</li>
              <li>Regular security training for employees</li>
              <li>Documented security policies and procedures</li>
              <li>Incident response and breach notification procedures</li>
              <li>Vendor security assessments and contractual security requirements</li>
            </ul>
            
            <h3>6.3 Physical Safeguards</h3>
            <ul>
              <li>Secure data centers with physical access controls</li>
              <li>Environmental controls (fire suppression, climate control, backup power)</li>
            </ul>
            
            <h3>6.4 Security Certifications</h3>
            <p>We maintain security certifications appropriate to our business. Contact <a href="mailto:security@fey.supply">security@fey.supply</a> for
            information about our current certifications, including SOC 2 Type II compliance reports available to enterprise customers under NDA.</p>
            
            <h3>6.5 Data Breach Notification</h3>
            <p>In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you
            and the relevant supervisory authorities without undue delay, and in any event within 72 hours of becoming aware of the breach,
            in accordance with applicable law.</p>
            
            <h3>6.6 No Guarantee</h3>
            <p>While we implement robust security measures, no method of transmission over the Internet or electronic storage is
            completely secure. We cannot guarantee absolute security of your personal information.</p>
          </article>
          <article className="legal-block">
            <h2>7. Your Privacy Rights</h2>
            <p>Depending on your location, you may have certain rights regarding your personal information. This section describes
            your rights and how to exercise them.</p>
            
            <h3>7.1 Rights Under Applicable Laws</h3>
            <p>You may have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request access to personal information we hold about you and receive a copy in a portable format</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to certain exceptions</li>
              <li><strong>Data Portability:</strong> Receive your personal information in a structured, commonly used, machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing of your personal information based on legitimate interests or for direct marketing</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances, such as pending verification of accuracy</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent you have previously provided, without affecting the lawfulness of processing before withdrawal</li>
            </ul>
            
            <h3>7.2 How to Exercise Your Rights</h3>
            <p>To exercise your rights, contact us at <a href="mailto:privacy@fey.supply">privacy@fey.supply</a> with your request. 
            You may also exercise certain rights directly through your account settings. We will respond to your request within 30 days.
            If we need more time, we will inform you of the reason and extension period.</p>
            
            <h3>7.3 Verification</h3>
            <p>To protect your privacy and security, we may need to verify your identity before responding to certain requests.
            We may request additional information or documentation to verify your identity.</p>
            
            <h3>7.4 Authorized Agents</h3>
            <p>You may designate an authorized agent to submit requests on your behalf. The agent must provide evidence of authorization,
            and we may require you to verify your identity separately.</p>
            
            <h3>7.5 Appeals</h3>
            <p>If we decline to act on your request, you may appeal our decision by contacting <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.
            We will respond to your appeal within 45 days.</p>
            
            <h3>7.6 Right to Lodge a Complaint</h3>
            <p>If you are located in the EEA, UK, or Switzerland, you have the right to lodge a complaint with your local data
            protection authority. Contact information for EEA supervisory authorities is available at 
            <a href="https://edpb.europa.eu/about-edpb/board/members_en" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a>.
            UK residents may contact the <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">Information Commissioner's Office</a>.</p>
          </article>
          <article className="legal-block">
            <h2>8. California Residents (CCPA/CPRA)</h2>
            <p>If you are a California resident, the California Consumer Privacy Act ("CCPA") and California Privacy Rights Act
            ("CPRA") provide you with additional rights. This section provides disclosures required by California law.</p>
            
            <h3>8.1 Personal Information Collected</h3>
            <p>During the preceding 12 months, we have collected the following categories of personal information:</p>
            <ul>
              <li>Identifiers (name, email address, phone number, account credentials)</li>
              <li>Professional or employment information (job title, pharmacy name, license number)</li>
              <li>Commercial information (transaction history, purchase records)</li>
              <li>Financial information (payment card details processed by payment processors)</li>
              <li>Internet or network activity information (browsing history, usage data)</li>
              <li>Geolocation data (approximate location from IP address)</li>
              <li>Sensory data (audio or images if you upload them)</li>
              <li>Inferences drawn from other personal information</li>
            </ul>
            
            <h3>8.2 Sources of Personal Information</h3>
            <p>We collect personal information from you directly, from your use of our Services, from third-party integrations
            you authorize, and from publicly available sources.</p>
            
            <h3>8.3 Business and Commercial Purposes</h3>
            <p>We use personal information for the business and commercial purposes described in Section 3.</p>
            
            <h3>8.4 Sharing of Personal Information</h3>
            <p>We disclose personal information to the categories of third parties described in Section 4, including service providers,
            suppliers (when you initiate communications), and as required by law.</p>
            
            <h3>8.5 Sale and Sharing</h3>
            <p><strong>We do not sell personal information</strong> as defined by the CCPA. We do not share personal information for
            cross-context behavioral advertising. We do not use sensitive personal information for purposes beyond providing our Services.</p>
            
            <h3>8.6 California Privacy Rights</h3>
            <p>As a California resident, you have the right to:</p>
            <ul>
              <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we collect, use, and share</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
              <li><strong>Right to Opt-Out:</strong> Not applicable as we do not sell or share personal information for cross-context behavioral advertising</li>
              <li><strong>Right to Limit:</strong> Not applicable as we do not use sensitive personal information for purposes beyond providing our Services</li>
              <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising your privacy rights</li>
            </ul>
            
            <h3>8.7 Exercising California Rights</h3>
            <p>To exercise your California privacy rights, contact us at <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.
            We will verify your request using the information associated with your account. You may also designate an authorized agent
            to submit requests on your behalf.</p>
            
            <h3>8.8 Shine the Light</h3>
            <p>Under California Civil Code Section 1798.83, you have the right to request information about our disclosure of
            personal information to third parties for their direct marketing purposes. We do not disclose personal information for
            third-party direct marketing purposes.</p>
          </article>
          
          <article className="legal-block">
            <h2>9. Nigerian Residents (NDPR)</h2>
            <p>If you are a resident of Nigeria, the Nigeria Data Protection Regulation 2019 ("NDPR") provides you with rights
            regarding your personal data. This section provides disclosures required by Nigerian law.</p>
            
            <h3>9.1 Data Controller</h3>
            <p>Fey Technologies is the data controller responsible for the processing of your personal data under the NDPR.
            Our registered address is 256 Chapman Road, Newark, Delaware 19702, United States.</p>
            
            <h3>9.2 Personal Data Processed</h3>
            <p>We process the categories of personal data described in Section 2, including account information, pharmacy data,
            usage information, and communication data.</p>
            
            <h3>9.3 Purpose of Processing</h3>
            <p>We process personal data for the purposes described in Section 3, including service delivery, AI features,
            security, service improvement, communications, and legal compliance.</p>
            
            <h3>9.4 Legal Basis for Processing</h3>
            <p>Under the NDPR, we process personal data based on:</p>
            <ul>
              <li>Your consent, where required</li>
              <li>Contractual necessity to provide our Services</li>
              <li>Legal obligation under applicable laws</li>
              <li>Legitimate interests that do not override your rights</li>
            </ul>
            
            <h3>9.5 Data Subject Rights</h3>
            <p>Under the NDPR, you have the right to:</p>
            <ul>
              <li>Request access to your personal data</li>
              <li>Request rectification of inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>Lodge a complaint with the National Information Technology Development Agency (NITDA)</li>
            </ul>
            
            <h3>9.6 Data Transfers Outside Nigeria</h3>
            <p>Your personal data may be transferred to countries outside Nigeria, including the United States. We ensure
            appropriate safeguards are in place for such transfers, as described in Section 13. In accordance with the NDPA
            and NDPR, we ensure that any country to which personal data is transferred provides an adequate level of protection,
            or that appropriate safeguards (such as standard contractual clauses or binding corporate rules) are in place.</p>

            <h3>9.7 Data Protection Compliance Organization</h3>
            <p>As required by the NDPR, we have filed our annual audit report with NITDA and maintain a Data Protection
            Compliance Organization (DPCO) to oversee our processing of Nigerian personal data. Our DPCO is responsible for
            ensuring ongoing compliance with the NDPR and NDPA.</p>
          </article>

          <article className="legal-block">
            <h2>10. Qatar Residents (PDPPL)</h2>
            <p>If you are a resident of Qatar, Qatar's Law No. 13 of 2016 on Personal Data Privacy Protection ("PDPPL")
            provides you with rights regarding your personal data. Fey operates from the Qatar Financial Center (QFC),
            which has its own data protection framework (QFC Data Protection Regulations 2021). This section provides
            disclosures relevant to Qatar-based users.</p>

            <h3>10.1 Controller and QFC Registration</h3>
            <p>Fey Technologies operates within the QFC and is subject to the QFC Data Protection Regulations 2021 as
            administered by the QFC Authority. We are registered with the QFC Authority as a data controller for the
            processing activities described in this Privacy Policy.</p>

            <h3>10.2 Personal Data Processed</h3>
            <p>We process the categories of personal data described in Section 2, including account information, pharmacy
            data, usage information, and communication data. We do not process "sensitive personal data" as defined by the
            PDPPL (including health data, religious beliefs, or biometric data) unless strictly necessary for the provision
            of our Services and with your explicit consent.</p>

            <h3>10.3 Legal Basis for Processing</h3>
            <p>Under the PDPPL and QFC Data Protection Regulations, we process personal data based on:</p>
            <ul>
              <li>Your consent, which you may withdraw at any time without affecting the lawfulness of prior processing</li>
              <li>Performance of a contract to which you are a party</li>
              <li>Compliance with legal obligations under Qatar law or QFC regulations</li>
              <li>Protection of your vital interests or those of another person</li>
              <li>Legitimate interests pursued by Fey, provided such interests do not override your fundamental rights</li>
            </ul>

            <h3>10.4 Data Subject Rights Under PDPPL</h3>
            <p>Under the PDPPL and QFC Data Protection Regulations, you have the right to:</p>
            <ul>
              <li>Be informed about the collection and processing of your personal data</li>
              <li>Access your personal data and obtain a copy</li>
              <li>Request correction of inaccurate or incomplete personal data</li>
              <li>Request deletion of your personal data where processing is no longer necessary</li>
              <li>Object to processing of your personal data in certain circumstances</li>
              <li>Restrict processing of your personal data pending verification or objection</li>
              <li>Data portability — receive your data in a structured, commonly used format</li>
            </ul>

            <h3>10.5 Cross-Border Transfers from Qatar</h3>
            <p>Personal data of Qatar residents may be transferred outside Qatar, including to the United States, in accordance
            with the PDPPL and QFC Data Protection Regulations. We ensure such transfers are protected by appropriate safeguards,
            including standard contractual clauses and data processing agreements that provide an adequate level of protection
            as required by Qatar law.</p>

            <h3>10.6 Complaints</h3>
            <p>If you are not satisfied with our handling of your personal data, you may lodge a complaint with the QFC Authority
            or the Compliance and Data Protection Department of the Ministry of Transport and Communications of Qatar.</p>
          </article>

          <article className="legal-block">
            <h2>11. Automated Decision-Making and Profiling</h2>
            <p>Our AI features provide recommendations and insights to support procurement decisions. This section describes
            how automated decision-making is used and your rights.</p>
            
            <h3>11.1 AI-Powered Features</h3>
            <p>We use AI and machine learning to:</p>
            <ul>
              <li>Generate procurement list recommendations based on inventory patterns and stock levels</li>
              <li>Predict expiry risks and stockout risks</li>
              <li>Suggest optimal order quantities and timing</li>
              <li>Analyze supplier performance and pricing patterns</li>
            </ul>
            
            <h3>11.2 Human Oversight</h3>
            <p><strong>All significant decisions remain under human control.</strong> AI-generated procurement lists and expiry
            alerts are suggestions that you review and approve. We do not make fully automated decisions that produce legal or
            similarly significant effects without human oversight.</p>
            
            <h3>11.3 Your Rights</h3>
            <p>Under applicable laws, including GDPR Article 22, you have the right:</p>
            <ul>
              <li>Not to be subject to decisions based solely on automated processing that produce legal or similarly significant effects</li>
              <li>To obtain human intervention if you are subject to such automated decisions</li>
              <li>To express your point of view and contest automated decisions</li>
              <li>To receive meaningful information about the logic involved and potential consequences of such processing</li>
            </ul>
            
            <h3>11.4 Requesting Human Review</h3>
            <p>If you would like human review of any AI-generated output, contact us at <a href="mailto:support@fey.supply">support@fey.supply</a>
            with "Human Review Request" in the subject line. We will review your request and respond within 5 business days.</p>

            <h3>11.5 AI Data Flow and Processing Pipeline</h3>
            <p>When you use AI-powered features, your data flows through the following pipeline:</p>
            <ul>
              <li><strong>Data Preparation:</strong> Relevant inventory, supplier, and procurement data from your workspace is selected and prepared for AI processing. Only data necessary for the specific query or recommendation is included.</li>
              <li><strong>Contextual Enrichment:</strong> Your pharmacy profile, branch memory (learned preferences), and pharmacy policies are added to provide context for accurate recommendations.</li>
              <li><strong>AI Processing:</strong> The prepared data is sent to our AI provider via encrypted API calls. The provider processes the request and returns results in real-time. No data is cached or retained by the AI provider after processing.</li>
              <li><strong>Result Delivery:</strong> AI-generated recommendations are returned to your workspace for your review. All recommendations are presented as suggestions, never executed automatically without your approval (unless you have explicitly enabled auto-procurement policies).</li>
              <li><strong>Audit Trail:</strong> We maintain audit logs of AI interactions, including what data was processed and what recommendations were generated, for transparency, debugging, and compliance purposes. These logs are retained for 24 months.</li>
            </ul>
            <p>You may request a detailed summary of what data was processed by our AI features for your account by contacting
            <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.</p>

            <h3>11.6 Profiling</h3>
            <p>We engage in profiling to the extent that our AI features analyze your inventory patterns, purchasing behavior,
            supplier preferences, and stock movement history to generate recommendations. This profiling is used solely to
            provide and improve our Services and does not produce legal effects or similarly significant effects on you.
            You may object to profiling at any time by contacting <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>,
            though this may limit the functionality of AI-powered features.</p>
          </article>
          <article className="legal-block">
            <h2>12. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to operate and improve our Services. This section describes
            the types of technologies we use and your choices.</p>
            
            <h3>12.1 Types of Technologies</h3>
            <p><strong>Cookies:</strong> Small text files stored on your device that help us recognize you and remember your preferences.</p>
            <p><strong>Web Beacons and Pixel Tags:</strong> Small graphics embedded in emails or web pages that help us track user behavior and measure effectiveness.</p>
            <p><strong>Local Storage:</strong> Browser storage technologies like localStorage and sessionStorage that store information on your device.</p>
            <p><strong>Analytics Tools:</strong> Third-party tools that help us understand how users interact with our Services.</p>

            <h3>12.2 Categories of Cookies</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Purpose</th>
                  <th style={{ textAlign: 'left', padding: '12px 8px' }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Essential</td>
                  <td style={{ padding: '12px 8px' }}>Required for the Services to function (authentication, security, session management)</td>
                  <td style={{ padding: '12px 8px' }}>Session to 1 year</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Functional</td>
                  <td style={{ padding: '12px 8px' }}>Remember your preferences and settings</td>
                  <td style={{ padding: '12px 8px' }}>Up to 1 year</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Analytics</td>
                  <td style={{ padding: '12px 8px' }}>Understand how users interact with our Services</td>
                  <td style={{ padding: '12px 8px' }}>Up to 2 years</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 8px' }}>Security</td>
                  <td style={{ padding: '12px 8px' }}>Detect fraud, prevent abuse, and protect against security threats</td>
                  <td style={{ padding: '12px 8px' }}>Up to 1 year</td>
                </tr>
              </tbody>
            </table>
            
            <h3>12.3 Third-Party Cookies</h3>
            <p>Some cookies may be placed by third-party service providers. These third parties may collect information about
            your online activities over time and across different websites. We do not control these third-party cookies.</p>
            
            <h3>12.4 Managing Cookies</h3>
            <p>You can manage cookie preferences through:</p>
            <ul>
              <li><strong>Browser Settings:</strong> Most browsers allow you to block, delete, or manage cookies through their settings. Note that blocking essential cookies may affect the functionality of our Services.</li>
              <li><strong>Do Not Track:</strong> Some browsers have a "Do Not Track" feature. Our Services currently do not respond to Do Not Track signals.</li>
              <li><strong>Global Privacy Control (GPC):</strong> If your browser or browser extension sends a GPC signal, we will treat it as a request to opt-out of the sale or sharing of personal information.</li>
              <li><strong>Opt-Out Links:</strong> For third-party analytics cookies, you may opt-out through the third-party's opt-out mechanisms.</li>
            </ul>
            
            <h3>12.5 Impact of Disabling Cookies</h3>
            <p>If you disable cookies, you may not be able to use certain features of our Services. Essential cookies cannot be
            disabled without affecting core functionality.</p>
          </article>
          
          <article className="legal-block">
            <h2>13. International Data Transfers and Children's Privacy</h2>
            <p>Your personal information may be transferred to and processed in countries other than your own. This section
            describes the safeguards we have in place for such transfers, as well as our policies regarding children's privacy.</p>

            <h3>13.1 Countries Where Data May Be Processed</h3>
            <p>We operate globally and may transfer personal information to the following countries and regions where we or our service
            providers have operations: United States (primary hosting and operations), Qatar (QFC operations), Nigeria (local operations),
            Ireland and other EU countries (service provider infrastructure), and other countries where our service providers maintain
            data centers or processing facilities. We select service providers and hosting locations with careful consideration of
            data protection standards and applicable adequacy decisions.</p>

            <h3>13.2 Safeguards for EEA, UK, and Swiss Transfers</h3>
            <p>For transfers from the EEA, UK, and Switzerland to countries without an adequacy determination from the European
            Commission, we rely on the following mechanisms:</p>
            <ul>
              <li><strong>Standard Contractual Clauses (SCCs):</strong> We use the European Commission's Standard Contractual Clauses (Module 1: Controller to Controller; Module 2: Controller to Processor) approved by Commission Implementing Decision 2021/914. You may request a copy of the SCCs by contacting <a href="mailto:dpo@fey.supply">dpo@fey.supply</a>.</li>
              <li><strong>Data Processing Agreements:</strong> We enter into data processing agreements with our service providers that include appropriate safeguards for international transfers, including obligations regarding data security, breach notification, and sub-processor management.</li>
              <li><strong>Supplementary Measures:</strong> Where required following transfer impact assessments, we implement supplementary technical and organizational measures such as encryption, pseudonymization, or additional access controls.</li>
              <li><strong>Consent:</strong> In limited cases, we may transfer data based on your explicit consent after informing you of the specific risks associated with the transfer.</li>
            </ul>

            <h3>13.3 UK and Swiss Transfers</h3>
            <p>For transfers from the UK, we use the UK International Data Transfer Agreement (IDTA) or the UK Addendum to the
            EU SCCs, as appropriate. For transfers from Switzerland, we use the Swiss Federal Data Protection Act (FADP) approved
            transfer mechanisms, or the equivalent safeguards.</p>

            <h3>13.4 Nigeria and Qatar Transfer Safeguards</h3>
            <p>For transfers of personal data from Nigeria, we comply with the NDPR and NDPA requirements for international transfers,
            ensuring the receiving country provides an adequate level of protection or that appropriate safeguards are in place.
            For transfers from Qatar, we comply with the PDPPL and QFC Data Protection Regulations requirements, including
            ensuring adequate protections in the receiving jurisdiction.</p>

            <h3>13.5 Transfer Impact Assessments</h3>
            <p>We conduct transfer impact assessments ("TIAs") for transfers to countries without adequacy determinations to ensure
            appropriate safeguards are in place and that your personal information is adequately protected. TIAs consider the laws
            of the destination country, the nature of the data transferred, and the technical and organizational measures in place.
            TIAs are reviewed and updated when there are material changes to the transfer arrangements or the legal landscape.</p>

            <h3>13.6 Binding Corporate Rules</h3>
            <p>We do not currently have approved Binding Corporate Rules. If we adopt BCRs in the future, we will update this section
            and provide notice to affected users.</p>

            <h3>13.7 Children's Privacy</h3>
            <p>Our Services are designed for professional pharmacy use and are not intended for individuals under 18 years of age.
            We do not knowingly collect personal information from children under 18. If we learn that we have collected personal
            information from a child under 18, we will take steps to delete that information as quickly as possible, and in any
            event within 30 days. If you believe we have collected personal information from a child, please contact us at
            <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>. If we operate in jurisdictions that require parental
            consent for the collection of personal information from minors, we will obtain such consent before collecting the
            information.</p>
          </article>

          <article className="legal-block">
            <h2>14. Changes, Updates, and Additional Disclosures</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technologies,
            legal requirements, or other factors.</p>

            <h3>14.1 Notice of Changes</h3>
            <p>We will notify you of material changes to this Privacy Policy by:</p>
            <ul>
              <li>Posting a prominent notice on our website at least 30 days before the change takes effect</li>
              <li>Sending you an email notification (if you have provided us with your email address)</li>
              <li>Providing in-app notification within the Service</li>
              <li>Updating the "Last updated" date at the top of this Privacy Policy</li>
            </ul>
            <p>For non-material changes (such as typographical corrections or formatting updates), we may update this Privacy Policy
            without advance notice, but we will always update the "Last updated" date.</p>

            <h3>14.2 Effective Date</h3>
            <p>Changes will be effective on the "Last updated" date shown at the top of this Privacy Policy. We will provide
            at least 30 days' notice before material changes take effect for existing users, where required by law. For changes
            that expand our use of personal information or reduce your rights, we will seek your consent where required by
            applicable law.</p>

            <h3>14.3 Acceptance of Changes</h3>
            <p>Your continued use of our Services after the effective date of changes constitutes acceptance of the updated
            Privacy Policy. If you do not agree with the changes, you may stop using our Services and request deletion of
            your personal information.</p>

            <h3>14.4 Version History</h3>
            <p>We maintain a version history of this Privacy Policy. Previous versions are available upon request by contacting
            <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.</p>

            <h3>14.5 Data Processing Agreement (DPA)</h3>
            <p>If you are a customer who requires a Data Processing Agreement under GDPR, CCPA, NDPR, PDPPL, or other applicable law,
            please contact us at <a href="mailto:legal@fey.supply">legal@fey.supply</a>. We will provide a DPA that sets out
            the terms of our processing relationship, including the categories of data processed, processing purposes, technical
            and organizational security measures, sub-processor obligations, and data breach notification procedures. DPAs are
            provided at no additional cost.</p>

            <h3>14.6 Record of Processing Activities</h3>
            <p>We maintain a record of processing activities as required by Article 30 of the GDPR and equivalent requirements
            under the NDPR and PDPPL. This record documents the categories of processing activities, data categories, recipients,
            international transfers, and retention periods. The record is available to supervisory authorities upon request.</p>

            <h3>14.7 Third-Party Links</h3>
            <p>Our Services may contain links to third-party websites or services. We are not responsible for the privacy
            practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.
            The inclusion of a link does not imply endorsement by Fey of the linked site or service.</p>

            <h3>14.8 Accessibility</h3>
            <p>We are committed to making this Privacy Policy accessible to all users. If you need this Privacy Policy in an
            alternative format, please contact us at <a href="mailto:privacy@fey.supply">privacy@fey.supply</a>.</p>
          </article>

          <article className="legal-block">
            <h2>15. Contact Us</h2>
            <p>For privacy inquiries, data requests, or to exercise your rights, please contact us using the appropriate
            channel below. We are committed to responding to all inquiries promptly and transparently.</p>

            <h3>15.1 Privacy Team</h3>
            <p>
              Email: <a href="mailto:privacy@fey.supply">privacy@fey.supply</a><br />
              Response Time: Within 30 days for data subject requests (extendable by 60 days for complex requests, with notice)
            </p>

            <h3>15.2 Data Protection Officer</h3>
            <p>
              Email: <a href="mailto:dpo@fey.supply">dpo@fey.supply</a><br />
              For questions about this Privacy Policy, data processing activities, DPIAs, or to lodge a complaint
            </p>

            <h3>15.3 EU Representative</h3>
            <p>
              Email: <a href="mailto:eu-rep@fey.supply">eu-rep@fey.supply</a><br />
              For EEA data subjects — contact point for GDPR Article 27 representative inquiries
            </p>

            <h3>15.4 Legal Team</h3>
            <p>
              Email: <a href="mailto:legal@fey.supply">legal@fey.supply</a><br />
              For legal inquiries, Data Processing Agreements, and regulatory matters
            </p>

            <h3>15.5 Security Team</h3>
            <p>
              Email: <a href="mailto:security@fey.supply">security@fey.supply</a><br />
              For security concerns, vulnerability reports, data breach notifications, or security certifications
            </p>

            <h3>15.6 Postal Addresses</h3>
            <p style={{ marginTop: 16 }}>
              <strong>Fey Technologies — United States (Headquarters)</strong><br />
              256 Chapman Road, Newark, Delaware 19702, United States
            </p>
            <p>
              <strong>Fey Technologies — Qatar (QFC)</strong><br />
              Qatar Financial Center, Doha, Qatar
            </p>

            <h3>15.7 Supervisory Authorities</h3>
            <p>If you are not satisfied with our response to a privacy concern, you have the right to lodge a complaint
            with your local data protection authority:</p>
            <ul>
              <li><strong>EEA:</strong> <a href="https://edpb.europa.eu/about-edpb/board/members_en" target="_blank" rel="noopener noreferrer">European Data Protection Board</a> — find your local supervisory authority</li>
              <li><strong>UK:</strong> <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO)</a></li>
              <li><strong>Switzerland:</strong> <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">Federal Data Protection and Information Commissioner (FDPIC)</a></li>
              <li><strong>Nigeria:</strong> <a href="https://nitda.gov.ng" target="_blank" rel="noopener noreferrer">National Information Technology Development Agency (NITDA)</a></li>
              <li><strong>Qatar:</strong> QFC Authority, Compliance and Data Protection Department, Ministry of Transport and Communications</li>
              <li><strong>California:</strong> <a href="https://oag.ca.gov/privacy" target="_blank" rel="noopener noreferrer">California Attorney General</a> and <a href="https://cppa.ca.gov" target="_blank" rel="noopener noreferrer">California Privacy Protection Agency (CPPA)</a></li>
            </ul>
          </article>
        </div>
      </section>
    </main>
  )
}

function TermsPage() {
  return (
    <main className="main">
      <section className="section section--legal">
        <div className="section__inner legal">
          <div className="section__header">
            <h1>Terms of Service</h1>
            <p className="section__lede">Effective date: February 23, 2026 | Last updated: February 23, 2026</p>
          </div>
          
          <article className="legal-block">
            <h2>1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Fey Technologies
              ("Fey", "we", "our", or "us") governing your access to and use of the Fey services, including our website,
              mobile applications, desktop applications, API services, and all related software and documentation
              (collectively, the "Service").
            </p>
            <p>
              <strong>Binding Agreement.</strong> By accessing or using the Service, you agree to be bound by these Terms.
              If you are using the Service on behalf of an organization, you represent and warrant that you have the
              authority to bind that organization to these Terms, and references to "you" in these Terms will refer to
              that organization.
            </p>
            <p>
              <strong>Acceptance.</strong> You accept these Terms by: (a) clicking to accept or agree to these Terms where
              this option is made available; (b) creating an account or using the Service; or (c) entering into an order
              form, statement of work, or other agreement that references these Terms.
            </p>
            <p>
              <strong>Additional Terms.</strong> Your use of the Service may also be subject to additional terms,
              including our <a href="/privacy">Privacy Policy</a>, acceptable use policies, service-level agreements,
              and other terms that may be posted on our website or provided to you. Those additional terms are
              incorporated by reference into these Terms.
            </p>
          </article>
          <article className="legal-block">
            <h2>2. Description of Service</h2>
            <p>
              Fey is an AI-powered pharmacy procurement assistant that helps pharmacies manage inventory, generate
              procurement lists, track expiry risks, and communicate with suppliers.
            </p>
            
            <h3>2.1 Service Components</h3>
            <p>The Service includes:</p>
            <ul>
              <li><strong>Inventory Management:</strong> Sync and manage inventory data across multiple pharmacy branches</li>
              <li><strong>AI-Powered Recommendations:</strong> Generate procurement list suggestions based on inventory patterns, stock levels, and expiry risks</li>
              <li><strong>Expiry and Stockout Alerts:</strong> Monitor and alert on products approaching expiry or at risk of stockout</li>
              <li><strong>Supplier Communication:</strong> Request for Quote (RFQ) workflows to communicate with suppliers</li>
              <li><strong>Supplier Analytics:</strong> Track supplier performance, pricing, and delivery patterns</li>
              <li><strong>Integrations:</strong> Connect with pharmacy management systems and other third-party services</li>
              <li><strong>Mobile and Desktop Applications:</strong> Access the Service via mobile and desktop applications</li>
              <li><strong>API Access:</strong> Programmatic access to the Service via APIs (subject to plan availability)</li>
            </ul>
            
            <h3>2.2 Decision-Support Tool</h3>
            <p>
              <strong>Important:</strong> Fey is a decision-support tool, not a replacement for professional judgment.
              AI-generated procurement lists, expiry predictions, and other recommendations are suggestions to assist
              your decision-making. You are solely responsible for:
            </p>
            <ul>
              <li>Validating all purchase decisions before placing orders</li>
              <li>Verifying the accuracy of inventory data and supplier information</li>
              <li>Ensuring compliance with applicable laws, regulations, and professional standards</li>
              <li>Reviewing AI-generated suggestions before acting on them</li>
            </ul>
            
            <h3>2.3 Service Changes</h3>
            <p>
              We may modify, suspend, or discontinue features of the Service at any time. For material changes that
              negatively affect core procurement functionality, we will provide at least 90 days' notice. We will not
              discontinue the core procurement features without at least 1 year's notice to paid subscribers.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>3. Account Registration and Security</h2>
            
            <h3>3.1 Registration</h3>
            <p>To use the Service, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and update your information to keep it accurate, current, and complete</li>
              <li>Not create accounts for purposes other than legitimate pharmacy business operations</li>
              <li>Not create multiple accounts to circumvent service limitations</li>
            </ul>
            
            <h3>3.2 Account Security</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the security of your account credentials, including passwords and API keys</li>
              <li>All activities that occur under your account, including activities by users you authorize</li>
              <li>Promptly notifying us of any unauthorized access, security breach, or other security incident</li>
              <li>Ensuring that each person who accesses the Service through your account complies with these Terms</li>
            </ul>
            
            <h3>3.3 User Access</h3>
            <p>
              You may authorize users within your organization to access the Service through your account. You are
              responsible for managing user access, including adding and removing users. You will ensure that all
              authorized users comply with these Terms.
            </p>
            
            <h3>3.4 Account Suspension</h3>
            <p>
              We may suspend your account if we detect suspicious activity, potential security breaches, or violations
              of these Terms. We will make reasonable efforts to notify you of any suspension and provide an opportunity
              to resolve the issue.
            </p>
          </article>
          <article className="legal-block">
            <h2>4. Acceptable Use</h2>
            <p>You agree not to use the Service or permit others to use the Service:</p>
            
            <h3>4.1 Prohibited Activities</h3>
            <ul>
              <li>For any unlawful purpose or in violation of any applicable laws or regulations</li>
              <li>To upload, transmit, or store malicious code, viruses, malware, or other harmful content</li>
              <li>To attempt to gain unauthorized access to any part of the Service, other accounts, or our systems</li>
              <li>To reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service, except as expressly permitted by applicable law</li>
              <li>To probe, scan, or test the vulnerability of our systems or networks</li>
              <li>To interfere with, disrupt, or impair the Service or our systems</li>
              <li>To spam, harass, abuse, or harm any person or entity</li>
              <li>To share account credentials with unauthorized users</li>
              <li>To resell, redistribute, or make the Service available to third parties without authorization</li>
              <li>To use the Service to build or support products or services competitive with Fey</li>
              <li>To use the Service in a way that violates intellectual property rights of others</li>
              <li>To process or store data that is subject to restrictions that are incompatible with these Terms</li>
            </ul>
            
            <h3>4.2 Content Restrictions</h3>
            <p>You agree not to upload, transmit, or store content that:</p>
            <ul>
              <li>Is unlawful, harmful, threatening, abusive, or defamatory</li>
              <li>Infringes the intellectual property rights of others</li>
              <li>Contains personal information of others without their consent</li>
              <li>Contains false, misleading, or deceptive information</li>
              <li>Violates applicable laws or regulations</li>
            </ul>
            
            <h3>4.3 Enforcement</h3>
            <p>
              We may investigate and take action against violations of this section, including suspending or terminating
              accounts, removing content, and reporting violations to law enforcement authorities.
            </p>
          </article>
          <article className="legal-block">
            <h2>5. Data and Content</h2>
            
            <h3>5.1 Your Data</h3>
            <p>
              <strong>Ownership.</strong> You retain ownership of all inventory data, supplier information, procurement
              lists, and other content you upload to or create within the Service ("User Data"). We do not claim any
              ownership rights in your User Data.
            </p>
            <p>
              <strong>License to Fey.</strong> You grant us a limited, non-exclusive license to access, process, and
              store your User Data solely to provide the Service to you. This license is limited to the duration of
              your use of the Service and terminates when you delete your User Data or your account.
            </p>
            <p>
              <strong>Responsibility for User Data.</strong> You are solely responsible for:
            </p>
            <ul>
              <li>The accuracy, completeness, and lawfulness of your User Data</li>
              <li>Ensuring you have all necessary rights, consents, and permissions to upload and process User Data</li>
              <li>Compliance with applicable laws, including healthcare and pharmaceutical regulations</li>
              <li>Maintaining appropriate backups of your User Data</li>
            </ul>
            
            <h3>5.2 AI-Generated Content</h3>
            <p>
              Procurement lists, recommendations, insights, and other content generated by our AI features are provided
              as suggestions to support your decision-making. You own the final decisions and any content you create
              based on these suggestions.
            </p>
            <p>
              <strong>AI Training.</strong> We do not use your User Data to train our AI models without your explicit
              consent. When you use AI-powered features, your data is processed to provide real-time insights but is
              not used to improve our underlying models.
            </p>
            
            <h3>5.3 Service Data</h3>
            <p>
              We may collect and use aggregated, anonymized data derived from your use of the Service ("Service Data")
              for purposes including improving our Service, developing new features, and conducting research. Service
              Data does not identify you or your organization and may be used by us without restriction.
            </p>
            
            <h3>5.4 Data Retention and Deletion</h3>
            <p>
              We retain User Data for as long as necessary to provide the Service and comply with legal obligations.
              Upon account deletion, we will delete your User Data within 90 days, except where retention is required
              by law. You may request earlier deletion by contacting us.
            </p>
            
            <h3>5.5 Data Export</h3>
            <p>
              You may export your User Data at any time through the Service's export functionality. We will provide
              data in commonly used formats (CSV, JSON) within a reasonable timeframe.
            </p>
          </article>
          <article className="legal-block">
            <h2>6. AI Features and Limitations</h2>
            
            <h3>6.1 Nature of AI Features</h3>
            <p>
              The Service includes AI-powered features that analyze your data and provide recommendations. These features
              are designed to assist, not replace, human decision-making.
            </p>
            <ul>
              <li><strong>Procurement Lists:</strong> AI analyzes inventory patterns, stock levels, and usage trends to suggest items to order</li>
              <li><strong>Expiry Predictions:</strong> AI identifies products at risk of expiry based on historical patterns and shelf life</li>
              <li><strong>Stockout Alerts:</strong> AI predicts potential stockouts based on usage patterns and lead times</li>
              <li><strong>Supplier Analytics:</strong> AI analyzes supplier performance, pricing, and reliability patterns</li>
            </ul>
            
            <h3>6.2 Limitations and Risks</h3>
            <p><strong>You acknowledge and agree that:</strong></p>
            <ul>
              <li>AI-generated recommendations are based on patterns in your data and may not account for all relevant factors</li>
              <li>AI features may occasionally produce inaccurate, incomplete, or unexpected results</li>
              <li>AI recommendations should not be relied upon as the sole basis for procurement decisions</li>
              <li>AI features may not account for regulatory requirements, contractual obligations, or business constraints</li>
              <li>The accuracy of AI recommendations depends on the quality and completeness of your data</li>
            </ul>
            
            <h3>6.3 Your Responsibilities</h3>
            <p>
              <strong>You are responsible for:</strong>
            </p>
            <ul>
              <li>Reviewing AI suggestions before acting on them</li>
              <li>Validating AI-generated recommendations against your professional judgment</li>
              <li>Ensuring decisions based on AI recommendations comply with applicable laws and regulations</li>
              <li>Providing feedback on AI accuracy to help improve the Service</li>
            </ul>
            
            <h3>6.4 AI Liability</h3>
            <p>
              We provide AI features as decision-support tools. While we strive for accuracy, we do not guarantee the
              accuracy, completeness, or suitability of AI-generated recommendations. You should validate all AI
              suggestions before acting on them. We are not liable for decisions you make based on AI recommendations.
            </p>
            
            <h3>6.5 Human Review</h3>
            <p>
              If you would like human review of any AI-generated output, you may contact our support team at
              <a href="mailto:support@fey.supply">support@fey.supply</a>. We will review your request within 5 business days.
            </p>
          </article>
          <article className="legal-block">
            <h2>7. Subscription and Payments</h2>
            
            <h3>7.1 Subscription Plans</h3>
            <p>
              Some features of the Service require a paid subscription. Available plans and pricing are displayed on
              our website or in your order form. Each plan has specific feature limitations, storage limits, and
              user counts as described in the plan documentation.
            </p>
            
            <h3>7.2 Billing and Payment</h3>
            <ul>
              <li><strong>Payment Method:</strong> You will provide a valid payment method (credit card or other accepted methods) when subscribing</li>
              <li><strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually, depending on your plan selection</li>
              <li><strong>Automatic Renewal:</strong> Subscriptions renew automatically at the end of each billing cycle unless cancelled before the renewal date</li>
              <li><strong>Authorization:</strong> You authorize us to charge your payment method for all fees associated with your subscription</li>
              <li><strong>Payment Processor:</strong> Payments are processed by third-party payment processors subject to their terms and conditions</li>
            </ul>
            
            <h3>7.3 Fees and Changes</h3>
            <ul>
              <li><strong>Current Pricing:</strong> Fees are as displayed on our website or in your order form at the time of subscription</li>
              <li><strong>Price Changes:</strong> We may change subscription fees with at least 30 days' notice to existing subscribers. Changes will take effect at the start of your next billing cycle</li>
              <li><strong>Plan Changes:</strong> You may upgrade or downgrade your plan at any time. Upgrades take effect immediately with prorated charges. Downgrades take effect at the start of your next billing cycle</li>
            </ul>
            
            <h3>7.4 Taxes</h3>
            <p>
              Fees do not include taxes unless otherwise stated. You are responsible for paying all taxes associated
              with your subscription, including sales tax, value-added tax (VAT), goods and services tax (GST), and
              similar charges. If we are required to collect taxes, we will add them to your invoice.
            </p>
            
            <h3>7.5 Refunds and Credits</h3>
            <p><strong>Refund Policy:</strong></p>
            <ul>
              <li>All fees are non-refundable except as required by law or as explicitly stated in these Terms</li>
              <li>Annual subscriptions may be eligible for prorated refunds within 14 days of initial subscription or renewal</li>
              <li>Monthly subscriptions do not qualify for refunds</li>
              <li>Service credits may be issued for documented service outages as described in our Service Level Agreement</li>
            </ul>
            
            <h3>7.6 Payment Failure</h3>
            <p>
              If payment fails, we will attempt to charge your payment method again. If payment continues to fail, we
              may suspend your account until payment is received. We are not responsible for any fees charged by your
              financial institution.
            </p>
            
            <h3>7.7 Free Trials and Promotions</h3>
            <p>
              We may offer free trials, promotional pricing, or other special offers. These offers are subject to
              specific terms and may be limited to new customers. We may terminate free trials at any time without
              notice.
            </p>
          </article>
          <article className="legal-block">
            <h2>8. Intellectual Property</h2>
            
            <h3>8.1 Fey's Intellectual Property</h3>
            <p>
              The Service, including its software, design, code, algorithms, databases, trademarks, trade names,
              logos, documentation, and all related intellectual property (collectively, "Fey Materials"), is owned
              by Fey and is protected by intellectual property laws, including copyright, trademark, patent, and
              trade secret laws.
            </p>
            <p>
              <strong>No License.</strong> These Terms do not grant you any rights to:
            </p>
            <ul>
              <li>Use our trademarks, trade names, or logos without our prior written consent</li>
              <li>Copy, modify, or create derivative works based on the Fey Materials</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Sublicense, rent, lease, or lend access to the Service</li>
            </ul>
            
            <h3>8.2 License to Use the Service</h3>
            <p>
              Subject to these Terms and your payment of applicable fees, we grant you a limited, non-exclusive,
              non-transferable, non-sublicensable license to access and use the Service for your internal business
              purposes during your subscription period.
            </p>
            
            <h3>8.3 Open Source Components</h3>
            <p>
              The Service may include or be distributed with open source software. Such software is subject to the
              applicable open source licenses, which will supersede these Terms with respect to that software.
            </p>
            
            <h3>8.4 Feedback</h3>
            <p>
              If you provide feedback, suggestions, ideas, or improvements regarding the Service ("Feedback"), you
              grant us a non-exclusive, royalty-free, worldwide, perpetual, irrevocable, fully transferable, and
              sublicensable license to use, modify, reproduce, distribute, and implement that Feedback without
              obligation to you. You waive any intellectual property claims in Feedback.
            </p>
          </article>
          <article className="legal-block">
            <h2>9. Service Level and Uptime</h2>
            
            <h3>9.1 Uptime Commitment</h3>
            <p>
              We target 99.5% monthly uptime for the Service. "Uptime" means the Service is operational and accessible
              for use, excluding scheduled maintenance and force majeure events.
            </p>
            
            <h3>9.2 Scheduled Maintenance</h3>
            <p>
              We may perform scheduled maintenance with at least 48 hours' notice. Scheduled maintenance will be
              performed during off-peak hours when possible. Downtime for scheduled maintenance does not count
              against uptime calculations.
            </p>
            
            <h3>9.3 Service Credits</h3>
            <p>
              If our monthly uptime falls below 99.5%, affected customers may request service credits:
            </p>
            <ul>
              <li>99.0% - 99.49% uptime: 5% credit of monthly subscription fee</li>
              <li>95.0% - 98.99% uptime: 10% credit of monthly subscription fee</li>
              <li>Below 95.0% uptime: 25% credit of monthly subscription fee</li>
            </ul>
            <p>
              To receive credits, you must request them within 30 days of the outage by contacting
              <a href="mailto:support@fey.supply">support@fey.supply</a> with documentation of the outage.
            </p>
            
            <h3>9.4 Exclusions</h3>
            <p>
              We are not responsible for downtime caused by: (a) factors outside our reasonable control; (b) your
              equipment, software, or internet connection; (c) your actions or omissions; (d) third-party services;
              or (e) scheduled maintenance.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE AND ALL FEY MATERIALS ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE.
            </p>
            <p>
              <strong>WE DO NOT WARRANT THAT:</strong>
            </p>
            <ul>
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>The Service will meet your specific requirements or expectations</li>
              <li>All errors or defects in the Service will be corrected</li>
              <li>The Service will be compatible with all devices or systems</li>
              <li>AI-generated recommendations will be accurate, complete, or suitable for your needs</li>
              <li>User Data will not be lost or corrupted</li>
              <li>The Service will be free of viruses, malware, or other harmful components</li>
            </ul>
            <p>
              SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES. THE ABOVE DISCLAIMERS MAY NOT APPLY
              TO YOU TO THE EXTENT PROHIBITED BY APPLICABLE LAW.
            </p>
          </article>
          <article className="legal-block">
            <h2>11. Limitation of Liability</h2>
            
            <h3>11.1 Exclusion of Consequential Damages</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL FEY, ITS AFFILIATES, OR THEIR RESPECTIVE
              DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or corruption of data</li>
              <li>Business interruption or loss of goodwill</li>
              <li>Cost of substitute services or products</li>
              <li>Cost of capital or financing</li>
              <li>Any other intangible or economic loss</li>
            </ul>
            <p>
              REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE OR WHETHER FEY WAS ADVISED OF THE POSSIBILITY OF
              SUCH DAMAGES.
            </p>
            
            <h3>11.2 Cap on Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL AGGREGATE LIABILITY FOR ANY AND ALL CLAIMS ARISING
              OUT OF OR RELATED TO THESE TERMS OR THE SERVICE, WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE),
              STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, SHALL NOT EXCEED THE GREATER OF:
            </p>
            <ul>
              <li>The total fees you paid to us for the Service in the 12 months preceding the claim; or</li>
              <li>$1,000 USD</li>
            </ul>
            
            <h3>11.3 Basis of the Bargain</h3>
            <p>
              The limitations in this section are a fundamental basis of the bargain between you and us. You acknowledge
              that the Service would not be provided without such limitations.
            </p>
            
            <h3>11.4 Exceptions</h3>
            <p>
              Some jurisdictions do not allow limitations on liability for certain types of damages. In those
              jurisdictions, our liability is limited to the maximum extent permitted by law.
            </p>
          </article>
          <article className="legal-block">
            <h2>12. Indemnification</h2>
            
            <h3>12.1 Your Indemnity Obligations</h3>
            <p>
              You agree to indemnify, defend, and hold harmless Fey, its affiliates, and their respective directors,
              officers, employees, agents, and service providers from and against any and all claims, damages, losses,
              liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your User Data, including claims that it infringes third-party rights</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of applicable laws or regulations</li>
              <li>Your violation of third-party rights, including intellectual property and privacy rights</li>
              <li>Your reliance on AI-generated recommendations</li>
            </ul>
            
            <h3>12.2 Indemnification Procedure</h3>
            <p>
              We will provide you with prompt notice of any claim subject to indemnification. You will have sole
              control over the defense and settlement of the claim, provided that any settlement that imposes
              obligations on us requires our prior written consent. We will cooperate with you in the defense
              of any claim at your expense.
            </p>
            
            <h3>12.3 Our Indemnification</h3>
            <p>
              We will indemnify, defend, and hold you harmless from claims that the Service, as provided by us,
              infringes any patent, copyright, or trademark of a third party, subject to the limitations in
              Section 11. If the Service becomes subject to an infringement claim, we may, at our option:
              (a) procure a license for your continued use; (b) modify the Service to avoid infringement; or
              (c) terminate your subscription with a prorated refund.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>13. Confidentiality</h2>
            
            <h3>13.1 Definition of Confidential Information</h3>
            <p>
              "Confidential Information" means any information disclosed by one party to the other that is marked
              as confidential or that would reasonably be understood to be confidential given its nature and the
              circumstances of disclosure. Your Confidential Information includes your User Data. Our Confidential
              Information includes the Service, documentation, and non-public information about our business.
            </p>
            
            <h3>13.2 Obligations</h3>
            <p>
              Each party agrees to: (a) protect the other's Confidential Information using the same degree of care
              used to protect its own confidential information, but no less than reasonable care; (b) use the
              Confidential Information only to exercise rights and fulfill obligations under these Terms; and
              (c) not disclose Confidential Information to third parties except as necessary to perform under
              these Terms.
            </p>
            
            <h3>13.3 Exceptions</h3>
            <p>
              Confidential Information does not include information that: (a) is or becomes publicly available
              without breach of these Terms; (b) was known to the receiving party before disclosure; (c) is
              independently developed without use of Confidential Information; or (d) is received from a third
              party without breach of obligation.
            </p>
            
            <h3>13.4 Compelled Disclosure</h3>
            <p>
              If required by law to disclose Confidential Information, the receiving party will provide prior
              notice to the disclosing party (if legally permitted) and reasonable cooperation in seeking
              protective orders.
            </p>
          </article>
          <article className="legal-block">
            <h2>14. Export Compliance</h2>
            <p>
              You agree not to use, export, re-export, or transfer the Service in violation of any applicable
              export laws, regulations, or sanctions, including U.S. export control laws and economic sanctions
              administered by the U.S. Department of Treasury's Office of Foreign Assets Control (OFAC).
            </p>
            <p>
              <strong>You represent and warrant that:</strong>
            </p>
            <ul>
              <li>You are not located in a country subject to a U.S. Government embargo or designated as a terrorist-supporting country</li>
              <li>You are not on any U.S. Government list of prohibited parties, including OFAC's Specially Designated Nationals list</li>
              <li>You will not use the Service for any purpose prohibited by U.S. export laws, including the design, development, or production of weapons</li>
            </ul>
          </article>
          
          <article className="legal-block">
            <h2>15. Data Processing</h2>
            <p>
              Our processing of your personal data is governed by our <a href="/privacy">Privacy Policy</a>, which
              is incorporated by reference into these Terms.
            </p>
            
            <h3>15.1 Data Processing Agreement</h3>
            <p>
              If you are a customer who requires a Data Processing Agreement (DPA) under GDPR, CCPA, or other
              applicable data protection laws, please contact us at <a href="mailto:legal@fey.supply">legal@fey.supply</a>.
              We will provide a DPA that sets out the terms of our processing relationship, including:
            </p>
            <ul>
              <li>Subject matter and duration of processing</li>
              <li>Nature and purpose of processing</li>
              <li>Categories of personal data and data subjects</li>
              <li>Technical and organizational security measures</li>
              <li>International data transfer mechanisms</li>
            </ul>
            
            <h3>15.2 Data Security</h3>
            <p>
              We implement industry-standard technical and organizational security measures to protect your data.
              See our <a href="/privacy">Privacy Policy</a> for details on our security practices.
            </p>
          </article>
          <article className="legal-block">
            <h2>16. Term and Termination</h2>
            
            <h3>16.1 Term</h3>
            <p>
              These Terms become effective when you accept them and remain in effect until terminated by either party.
            </p>
            
            <h3>16.2 Termination by You</h3>
            <p>
              You may terminate your subscription at any time through your account settings or by contacting us at
              <a href="mailto:support@fey.supply">support@fey.supply</a>. Termination takes effect at the end of your
              current billing period. You will not receive a refund for any remaining subscription period except as
              provided in Section 7.5.
            </p>
            
            <h3>16.3 Termination by Us</h3>
            <p>We may suspend or terminate your access to the Service:</p>
            <ul>
              <li>For cause, immediately upon notice if you materially breach these Terms and fail to cure within 30 days of notice</li>
              <li>For non-payment, after providing notice and opportunity to cure</li>
              <li>Immediately if your conduct causes harm to us, other users, or the Service</li>
              <li>Immediately if required by law or to protect our rights or safety</li>
              <li>For any reason with 60 days' notice (90 days for paid subscribers)</li>
            </ul>
            
            <h3>16.4 Effect of Termination</h3>
            <p>Upon termination:</p>
            <ul>
              <li>Your right to access and use the Service terminates immediately</li>
              <li>We will delete your User Data within 90 days, except where retention is required by law</li>
              <li>You may request an export of your User Data within 30 days of termination</li>
              <li>All fees owed become immediately due</li>
              <li>Provisions that by their nature should survive termination will remain in effect</li>
            </ul>
            
            <h3>16.5 Survival</h3>
            <p>
              The following sections survive termination: 5 (Data and Content), 8 (Intellectual Property), 10 (Disclaimer
              of Warranties), 11 (Limitation of Liability), 12 (Indemnification), 13 (Confidentiality), 14 (Export Compliance),
              16.4, 16.5, 18 (Governing Law), 19 (General Terms).
            </p>
          </article>
          <article className="legal-block">
            <h2>17. Changes to Terms</h2>
            <p>
              We may modify these Terms from time to time to reflect changes in our Service, legal requirements, or
              business practices.
            </p>
            
            <h3>17.1 Notice of Changes</h3>
            <p>
              We will notify you of material changes by: (a) posting a notice on our website; (b) sending you an email;
              or (c) providing in-app notification. For material changes that negatively affect your rights, we will
              provide at least 30 days' notice before changes take effect.
            </p>
            
            <h3>17.2 Acceptance of Changes</h3>
            <p>
              Your continued use of the Service after the effective date of changes constitutes acceptance of the
              modified Terms. If you do not agree with the changes, you may terminate your subscription as described
              in Section 16.
            </p>
            
            <h3>17.3 Version History</h3>
            <p>
              Previous versions of these Terms are available upon request. Contact us at
              <a href="mailto:legal@fey.supply">legal@fey.supply</a>.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>18. Governing Law and Dispute Resolution</h2>
            
            <h3>18.1 Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of Delaware, United
              States, without regard to its conflict of law principles. The United Nations Convention on Contracts
              for the International Sale of Goods (CISG) does not apply.
            </p>
            
            <h3>18.2 Venue</h3>
            <p>
              Any legal action or proceeding arising out of or related to these Terms shall be brought exclusively
              in the state or federal courts located in New Castle County, Delaware. Each party consents to the
              personal jurisdiction of such courts and waives any objection to venue.
            </p>
            
            <h3>18.3 Arbitration (Optional)</h3>
            <p>
              Either party may elect to resolve disputes through binding arbitration administered by the American
              Arbitration Association under its Commercial Arbitration Rules. The arbitration shall be conducted
              in Wilmington, Delaware. The arbitrator may award the same remedies a court could award. This
              arbitration provision does not apply to claims for injunctive or equitable relief.
            </p>
            
            <h3>18.4 Class Action Waiver</h3>
            <p>
              You agree not to participate in class actions, class arbitrations, or representative actions against us.
              You may only bring claims against us in your individual capacity.
            </p>
            
            <h3>18.5 Attorneys' Fees</h3>
            <p>
              In any action or proceeding to enforce rights under these Terms, the prevailing party is entitled to
              recover its reasonable attorneys' fees and costs.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>19. Force Majeure</h2>
            <p>
              Neither party shall be liable for any failure or delay in performing its obligations under these Terms
              if such failure or delay results from circumstances beyond its reasonable control, including but not
              limited to: acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military
              authorities, fire, floods, accidents, pandemic, strikes, or shortages of transportation, facilities,
              fuel, energy, labor, or materials.
            </p>
            <p>
              The affected party shall provide prompt notice of the force majeure event and its expected duration.
              If the force majeure event continues for more than 30 days, either party may terminate these Terms
              without liability.
            </p>
          </article>
          <article className="legal-block">
            <h2>20. General Terms</h2>
            
            <h3>20.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy and any other agreements incorporated by reference,
              constitute the entire agreement between you and us regarding the Service and supersede all prior
              and contemporaneous agreements, understandings, negotiations, and discussions, whether oral or written.
            </p>
            
            <h3>20.2 Severability</h3>
            <p>
              If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining
              provisions shall continue in full force and effect. The invalid provision shall be modified to the
              minimum extent necessary to make it valid, legal, and enforceable while preserving the parties'
              original intent.
            </p>
            
            <h3>20.3 Waiver</h3>
            <p>
              No failure or delay by either party in exercising any right under these Terms shall constitute a waiver
              of that right. Any waiver must be in writing and signed by the waiving party to be effective.
            </p>
            
            <h3>20.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or any rights hereunder without our prior written consent.
              We may assign these Terms to any affiliate or successor without your consent. Any attempted assignment
              in violation of this section is void.
            </p>
            
            <h3>20.5 Relationship of Parties</h3>
            <p>
              These Terms do not create a partnership, joint venture, agency, or employment relationship between the
              parties. You are an independent contractor, and nothing in these Terms creates any authority for you
              to act on our behalf.
            </p>
            
            <h3>20.6 No Third-Party Beneficiaries</h3>
            <p>
              These Terms are for the benefit of the parties only and do not create any rights in any third party.
              No third party may enforce any provision of these Terms.
            </p>
            
            <h3>20.7 Notices</h3>
            <p>
              All notices under these Terms shall be in writing. Notices to us shall be sent to
              <a href="mailto:legal@fey.supply">legal@fey.supply</a>. Notices to you shall be sent to the email
              address associated with your account. Notices are deemed received: (a) immediately when sent by email;
              (b) one business day after sending by overnight courier; or (c) three business days after mailing by
              first-class mail.
            </p>
            
            <h3>20.8 Export and Import Requirements</h3>
            <p>
              You are responsible for complying with all export and import requirements applicable to your use of
              the Service.
            </p>
            
            <h3>20.9 Government Use</h3>
            <p>
              If you are a U.S. government entity, the Service is provided with "commercial computer software" and
              "commercial computer software documentation" as those terms are used in the U.S. Federal Acquisition
              Regulation. Use, reproduction, or disclosure by the U.S. government is subject to the restrictions
              set forth in FAR 12.212 and DFARS 227.7202.
            </p>
            
            <h3>20.10 Equitable Remedies</h3>
            <p>
              You acknowledge that a breach of Sections 4 (Acceptable Use) or 13 (Confidentiality) may cause us
              irreparable harm that cannot be adequately compensated by money damages. We are entitled to seek
              injunctive relief to prevent or stop such breaches without the need to post a bond.
            </p>
          </article>
          
          <article className="legal-block">
            <h2>21. Contact Us</h2>
            <p>For questions about these Terms, please contact us:</p>
            
            <h3>21.1 Legal Inquiries</h3>
            <p>
              Email: <a href="mailto:legal@fey.supply">legal@fey.supply</a><br />
              For questions about these Terms, agreements, or legal matters
            </p>
            
            <h3>21.2 Support</h3>
            <p>
              Email: <a href="mailto:support@fey.supply">support@fey.supply</a><br />
              For technical support, account issues, or general inquiries
            </p>
            
            <h3>21.3 General Inquiries</h3>
            <p>
              Email: <a href="mailto:hello@fey.supply">hello@fey.supply</a><br />
              For general questions about Fey and our services
            </p>
            
            <h3>21.4 Postal Address</h3>
            <p style={{ marginTop: 16 }}>
              <strong>Fey Technologies</strong><br />
              256 Chapman Road, Newark, Delaware 19702, United States<br />
              Qatar Financial Center, Doha, Qatar
            </p>
          </article>
        </div>
      </section>
    </main>
  )
}

export function LandingApp() {
  const [route, setRoute] = useState<RoutePath>(() =>
    typeof window === 'undefined' ? '/' : normalizeRoute(window.location.pathname),
  )
  const [signup, setSignup] = useState<{ open: boolean; request: SignupRequest }>({
    open: false,
    request: { type: '', intent: 'download' },
  })
  const [marketOptions, setMarketOptions] = useState<MarketProfile[]>(DEFAULT_MARKETS)
  const [fxRates, setFxRates] = useState<Record<string, number>>(FALLBACK_USD_RATES)
  const [marketId, setMarketId] = useState<MarketId>('US')
  const [integrationTiles, setIntegrationTiles] = useState<IntegrationTile[]>([])

  useEffect(() => {
    const onPopState = () => setRoute(normalizeRoute(window.location.pathname))
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    let active = true
    void Promise.all([fetchAllMarkets(), fetchFxRates(), detectMarketIdByIp()]).then(([markets, rates, ipCode]) => {
      if (!active) return
      setMarketOptions(markets)
      setFxRates(rates)
      if (ipCode && markets.some((entry) => entry.id === ipCode)) {
        setMarketId(ipCode)
      }
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let active = true

    void listLandingIntegrationCatalog().then((catalog) => {
      if (!active) return
      const seen = new Set<string>()
      const tiles = catalog
        .filter((item) => {
          if (seen.has(item.key)) return false
          seen.add(item.key)
          return true
        })
        .map((item) => ({
          key: item.key,
          name: item.name,
          abbr: toAbbreviation(item.name),
          icon: INTEGRATION_ICONS[item.key] ?? '',
          color: colorFromKey(item.key),
          status: integrationStatusToBadge(item.status),
        }))
      setIntegrationTiles(tiles)
    })

    return () => {
      active = false
    }
  }, [])

  const navigate = (path: RoutePath) => {
    if (path === route) return
    window.history.pushState({}, '', path)
    setRoute(path)
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const openSignup = useCallback((type: string, intent: SignupIntent = 'download') => {
    setSignup({ open: true, request: { type, intent } })
  }, [])

  const closeSignup = useCallback(() => {
    setSignup((s) => ({ ...s, open: false }))
  }, [])

  const isVariantShell = route === '/' || route === '/pricing'

  return (
    <SignupContext.Provider value={{ openSignup }}>
      <div className="marketing-root">
        <div className={`page${isVariantShell ? ' page--variant' : ''}`}>
          {!isVariantShell && (
            <AppHeader route={route} onNavigate={navigate} />
          )}

          {route === '/' && (
            <LandingVariantPage
              onNavigate={navigate}
              marketId={marketId}
              onMarketIdChange={setMarketId}
              marketOptions={marketOptions}
              fxRates={fxRates}
              integrationTiles={integrationTiles}
            />
          )}
          {route === '/pricing' && (
            <PricingPage
              onNavigate={navigate}
              marketId={marketId}
              onMarketIdChange={setMarketId}
              marketOptions={marketOptions}
              fxRates={fxRates}
            />
          )}
          {route === '/privacy' && <PrivacyPage />}
          {route === '/terms' && <TermsPage />}

          {isVariantShell && <div className="variant-stripe footer-stripe" aria-hidden="true" />}
          <footer className={`footer${isVariantShell ? ' footer--variant' : ''}`}>
            <div className="footer__brand">
              <BrandLockup logoClassName="footer__logo" nameClassName="footer__name" />
            </div>
            <div className="footer__links">
              <a href="mailto:hello@fey.supply">hello@fey.supply</a>
              <a
                href="/privacy"
                onClick={(event) => {
                  event.preventDefault()
                  navigate('/privacy')
                }}
              >
                Privacy
              </a>
              <a
                href="/terms"
                onClick={(event) => {
                  event.preventDefault()
                  navigate('/terms')
                }}
              >
                Terms
              </a>
            </div>
          </footer>
        </div>

        <SignupModal
          isOpen={signup.open}
          request={signup.request}
          onClose={closeSignup}
          integrationTiles={integrationTiles}
        />
      </div>
    </SignupContext.Provider>
  )
}
